/** 产品模板编辑页（独立页面，不复用发布产品页组件） */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { localizeOptions } from '@/i18n/chartText';
import { SHEIN_QTY_KEYS, SKU_PKG_TYPE_KEYS, TITLE_LANG_KEYS } from '@/i18n/pkgOptionKeys';
import RequiredMark from '@/shein-platform/components/RequiredMark';
import PublishScrollJumpFab from '@/components/publish/PublishScrollJumpFab';
import AttributeField from '@/shein-platform/modules/products/components/AttributeField';
import CategoryCascaderSelect from '@/shein-platform/components/CategoryCascaderSelect';
import SearchableSelect from '@/shein-platform/modules/products/components/SearchableSelect';
import SearchableMultiSelect from '@/shein-platform/modules/products/components/SearchableMultiSelect';
import SheinDateTimePicker from '@/shein-platform/modules/products/components/SheinDateTimePicker';
import SpecSupplySection from '@/shein-platform/modules/products/components/SpecSupplySection';
import SizeChartPanel from '@/shein-platform/modules/products/components/SizeChartPanel';
import PackageBatchModal, {
  WEIGHT_UNIT_OPTIONS,
  DIM_UNIT_OPTIONS,
  QUANTITY_UNIT_OPTIONS,
  PACKAGE_TYPE_OPTIONS,
} from '@/shein-platform/modules/products/components/PackageBatchModal';
import { applyPackageBatchBySize, applyPackageBatchToAllSkus } from '@/shein-platform/utils/sheinPackageBatch';
import SheinSwitch from '@/shein-platform/components/SheinSwitch';
import SaveAttributeTemplateMetaModal from '@/shein-platform/modules/templates/SaveAttributeTemplateMetaModal';
import { buildSheinPath, openSheinPathInNewTab } from '@/shein-platform/constants/sheinRoutes';
import { isOfficialProductTemplateScope } from '@/shein-platform/constants/sheinProductTemplateScope';
import { useSheinShopOptional } from '@/shein-platform/context/SheinShopContext';
import { flattenBrandRows, flattenIpRows } from '@/shein-platform/utils/sheinBrandIp';
import { flattenLeafCategories } from '@/shein-platform/utils/sheinCategoryTree';
import {
  calcPublishCompletion,
  parseAttributeTemplate,
  partitionProductAttrsForDisplay,
  attributeDisplayLabel,
  attributeValueDisplayLabel,
} from '@/shein-platform/utils/sheinAttributeTemplate';
import { layoutAttributeFields } from '@/shein-platform/utils/sheinAttributeLayout';
import { isSheinAttrRequiredMark } from '@/shein-platform/utils/sheinRequiredAttrGroups';
import { parseCustomAttributePermission } from '@/shein-platform/utils/sheinCustomAttribute';
import {
  applyProductAttributeTemplatePayload,
  countFilledProductAttributes,
  alignAttributesToCategoryTemplate,
  filterProductAttributesToCategory,
  pickFilledProductAttributes,
  remapProductAttributesAcrossCategories,
  remapSizeChartAcrossCategories,
  remapSkcListAcrossCategories,
} from '@/shein-platform/utils/sheinProductAttributeTemplate';
import { writeProductTemplatePublishHandoff } from '@/shein-platform/utils/sheinProductTemplatePublishHandoff';
import {
  applyPrintNamesToSheinForm,
  enrichSheinFormPrintNames,
  printNamesFromSheinPrintImages,
} from '@/pages/ipe/workbench/utils/applyListingItemToSheinPublishForm';
import { pickAutoRuleTemplate } from '@/shein-platform/utils/sheinTemplateAutoRule';
import { nextTemplatePriority } from '@/shein-platform/utils/sheinTemplatePriority';
import { styleNoFromSupplierCode } from '@/shein-platform/utils/sheinStyleNo';
import {
  checkTemplateVersions,
  collectAppliedTemplateRefs,
  conflictFieldKey,
  conflictToastMessage,
  extractAttrTemplatePatch,
  extractSizeChartTemplatePatch,
  isMissingTemplateError,
  isTemplateVersionConflictError,
  parseTemplateVersion,
  partitionTemplateVersionConflicts,
  TEMPLATE_VERSION_TYPES,
} from '@/shein-platform/utils/sheinTemplateVersionGuard';
import {
  diffProductAttributeChanges,
  diffSizeChartChanges,
  indexAttributeChangeHints,
} from '@/shein-platform/utils/sheinTemplateChangeDiff';
import TemplateVersionConflictMark from '@/shein-platform/modules/products/components/TemplateVersionConflictMark';
import TemplateChangeDiffList from '@/shein-platform/modules/products/components/TemplateChangeDiffList';
import {
  customLabelFromKey,
  isCustomSpecKey,
  skuSizeKey,
} from '@/shein-platform/utils/sheinSizeChart';
import {
  collectProductAttributePairs,
  isValueAllowedByLinkedRule,
  parseAssociatedAttributeRules,
} from '@/shein-platform/utils/sheinAssociatedAttributeRules';
import {
  EMPTY_SKU_PACKAGE_CAPS,
  pickPublishCurrency,
  resolveSkuPackageCapabilities,
} from '@/shein-platform/utils/sheinPublishFillInStandard';
import { sheinShopCapabilities } from '@/shein-platform/utils/sheinShopCapabilities';
import {
  SHEIN_SHOP_TYPE_OPTIONS,
  normalizeSheinShopType,
} from '@/utils/platformShopCatalog';
import { loadShopPublishCurrencies } from '@/shein-platform/utils/resolveSheinPublishCurrency';
import { listEnabledSites, pickSiteListCurrency } from '@/shein-platform/utils/sheinSiteList';
import {
  persistSheinFormImageSlots,
  sanitizeSheinFormImageSlots,
} from '@/shein-platform/utils/sanitizeSheinFormImageSlots';
import { sheinPlatformAPI } from '@/utils/api';
import { adminAPI } from '@/utils/adminApi';
import { showToast } from '@/utils/toast';

function fetchAttrTemplateDetail(officialMode, shopId, id, extra = {}) {
  if (officialMode) {
    return adminAPI.getOfficialSheinAttributeTemplate(id);
  }
  return sheinPlatformAPI.getProductAttributeTemplate(shopId, id, extra);
}

function fetchAttrTemplateList(officialMode, shopId, params) {
  if (officialMode) {
    return adminAPI.listOfficialSheinAttributeTemplates(params);
  }
  return sheinPlatformAPI.listProductAttributeTemplates(shopId, params);
}

function fetchCategoryAttributeSchema(officialMode, shopId, productTypeId, categoryId, extra = {}) {
  const body = { product_type_id_list: [Number(productTypeId)] };
  const cat = Number(categoryId);
  if (Number.isFinite(cat) && cat > 0) body.category_id = cat;
  if (officialMode) {
    if (extra.shopType) body.shopType = extra.shopType;
    if (extra.categoryPathLabel) body.categoryPathLabel = extra.categoryPathLabel;
    return adminAPI.getOfficialSheinCatalogAttributeTemplate(body);
  }
  return sheinPlatformAPI.getAttributeTemplate(shopId, body, { forceRefresh: true });
}

function fetchSizeChartTemplateDetail(officialMode, shopId, id, extra = {}) {
  if (officialMode) {
    return adminAPI.getOfficialSheinSizeChartTemplate(id);
  }
  return sheinPlatformAPI.getSizeChartTemplate(shopId, id, extra);
}

function FieldErrorText({ message }) {
  if (!message) return null;
  return <div className="spo-field__error" role="alert">{message}</div>;
}

const TITLE_LANGUAGE_OPTIONS = [
  { value: 'en', label: '英语' },
  { value: 'zh-cn', label: '简体中文' },
  { value: 'de', label: '德语' },
  { value: 'fr', label: '法语' },
  { value: 'es', label: '西班牙语' },
  { value: 'it', label: '意大利语' },
  { value: 'pt', label: '葡萄牙语' },
  { value: 'ja', label: '日语' },
  { value: 'ko', label: '韩语' },
];

const ORIGIN_OPTIONS = [
  'China', 'Vietnam', 'Bangladesh', 'India', 'Turkey', 'Indonesia', 'Cambodia', 'Pakistan', 'United States', 'Other',
];

const SECTIONS = [
  { id: 'basic', title: '基础信息' },
  { id: 'attrs', title: '类目属性' },
  { id: 'spec', title: '规格供应' },
  { id: 'size', title: '尺码包装' },
  { id: 'sales', title: '销售发货' },
];

const SECTION_TITLE_KEYS = {
  basic: 'publishChrome.sectionBasic',
  attrs: 'publishChrome.sectionAttrs',
  spec: 'publishChrome.sectionSpec',
  size: 'publishChrome.sectionSizeChart',
  sales: 'publishChrome.sectionSales',
};

function deriveSelectedSizeIds(skcList = []) {
  const ids = [];
  const seen = new Set();
  skcList.forEach((skc) => {
    (skc.skuRows || []).forEach((sku) => {
      const key = skuSizeKey(sku);
      if (key && !seen.has(key)) {
        seen.add(key);
        ids.push(key);
      }
    });
  });
  return ids;
}

function parseSizeSelectionKey(sizeId) {
  const key = String(sizeId || '');
  if (isCustomSpecKey(key)) {
    const label = customLabelFromKey(key);
    return {
      sizeValueId: '',
      sizeIsCustom: true,
      sizeExtraValue: label,
      sizeLabel: label,
    };
  }
  return {
    sizeValueId: key,
    sizeIsCustom: false,
    sizeExtraValue: '',
    sizeLabel: '',
  };
}

function buildSkuRowsForSizes(existingRows = [], sizeIds = [], sizeAttr, defaults = {}) {
  if (!sizeIds.length) {
    // 编辑回填：已有多行 SKU 时绝不能压成一行（模板过滤尺码后 sizeIds 可能暂时为空）
    if (existingRows.length > 1) {
      return existingRows.map((row) => ({
        ...row,
        sizeAttributeId: sizeAttr?.attributeId || row.sizeAttributeId || '',
      }));
    }
    // 未选尺码时保留一行空 SKU，便于仅按颜色填价库存
    const first = existingRows[0];
    return [first
      ? {
        ...first,
        sizeValueId: '',
        sizeIsCustom: false,
        sizeExtraValue: '',
        sizeLabel: '',
        sizeAttributeId: sizeAttr?.attributeId || first.sizeAttributeId || '',
      }
      : createSkuRow({
        sizeAttributeId: sizeAttr?.attributeId || '',
        price: defaults.price || '',
        stock: defaults.stock || '',
        weight: defaults.weight || '',
        currency: defaults.currency || '',
      })];
  }
  return sizeIds.map((sizeId) => {
    const parsed = parseSizeSelectionKey(sizeId);
    const found = existingRows.find((row) => skuSizeKey(row) === String(sizeId));
    if (found) {
      return {
        ...found,
        sizeAttributeId: sizeAttr?.attributeId || found.sizeAttributeId || '',
        sizeValueId: parsed.sizeValueId,
        sizeIsCustom: parsed.sizeIsCustom,
        sizeExtraValue: parsed.sizeExtraValue,
        sizeLabel: parsed.sizeLabel
          || found.sizeLabel
          || sizeAttr?.values?.find((v) => String(v.id) === parsed.sizeValueId)?.name
          || '',
      };
    }
    const platformName = !parsed.sizeIsCustom
      ? (sizeAttr?.values?.find((v) => String(v.id) === parsed.sizeValueId)?.name || '')
      : parsed.sizeLabel;
    return createSkuRow({
      sizeAttributeId: sizeAttr?.attributeId || '',
      sizeValueId: parsed.sizeValueId,
      sizeIsCustom: parsed.sizeIsCustom,
      sizeExtraValue: parsed.sizeExtraValue,
      sizeLabel: platformName,
      price: defaults.price || '',
      stock: defaults.stock || '',
      weight: defaults.weight || '',
      currency: defaults.currency || '',
    });
  });
}

function createSkuRow(partial = {}) {
  return {
    id: `sku-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    sizeAttributeId: '',
    sizeValueId: '',
    sizeIsCustom: false,
    sizeExtraValue: '',
    sizeLabel: '',
    supplierSku: '',
    price: '',
    specialPrice: '',
    currency: '',
    stock: '',
    weight: '',
    length: '',
    width: '',
    height: '',
    quantity: '',
    quantityUnit: '1',
    packageType: '',
    mallState: 1,
    skuImage: null,
    skuCode: '',
    ...partial,
  };
}

function createSkc(partial = {}) {
  return {
    id: `skc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    colorAttributeId: '',
    colorValueId: '',
    colorLabel: '',
    colorCode: '',
    colorIsCustom: false,
    colorExtraValue: '',
    supplierCode: '',
    skcName: '',
    mainImage: null,
    showroomSlots: {},
    detailImages: [],
    squareImage: null,
    colorBlockImage: null,
    skuRows: [createSkuRow()],
    ...partial,
  };
}

function deriveFormCurrency(initial = {}) {
  const top = String(initial.currency || '').trim();
  if (top) return top;
  for (const skc of initial.skcList || []) {
    for (const sku of skc.skuRows || []) {
      const code = String(sku?.currency || '').trim();
      if (code) return code;
    }
  }
  return '';
}


function normalizeProductAttributes(raw = {}) {
  const out = {};
  Object.entries(raw || {}).forEach(([id, value]) => {
    if (value == null || value === '') return;
    if (Array.isArray(value)) {
      const asComposition = value.some((row) => (
        row && typeof row === 'object' && ('percent' in row || 'attributeExtraValue' in row || 'valueId' in row)
      ));
      if (asComposition) {
        out[id] = value
          .map((row) => {
            if (row != null && (typeof row === 'string' || typeof row === 'number')) {
              return { valueId: String(row), percent: '' };
            }
            if (!row || typeof row !== 'object') return null;
            const valueId = row.valueId ?? row.attributeValueId;
            if (valueId == null || valueId === '') return null;
            return {
              valueId: String(valueId),
              percent: String(row.percent ?? row.attributeExtraValue ?? ''),
            };
          })
          .filter(Boolean);
        return;
      }
      out[id] = value
        .map((row) => {
          if (row && typeof row === 'object') return String(row.valueId ?? row.attributeValueId ?? '');
          return String(row ?? '');
        })
        .filter(Boolean);
      return;
    }
    if (typeof value === 'object') {
      const valueId = value.valueId ?? value.attributeValueId;
      const percent = value.percent ?? value.attributeExtraValue;
      if (valueId != null && valueId !== '') {
        // 有比例（含 0）或显式 percent 字段 → 成分行；否则先保留 valueId，模板加载后再按 mode 转换
        if (percent != null && String(percent).trim() !== '') {
          out[id] = [{ valueId: String(valueId), percent: String(percent) }];
        } else {
          out[id] = String(valueId);
        }
      } else if (value.attributeExtraValue != null && String(value.attributeExtraValue).trim() !== '') {
        out[id] = String(value.attributeExtraValue);
      }
      return;
    }
    out[id] = value;
  });
  return out;
}

function emptyForm(initial = {}) {
  return {
    spuName: initial.spuName || '',
    title: initial.title || '',
    titleLanguage: initial.titleLanguage || 'en',
    description: initial.description || '',
    categoryId: initial.categoryId || '',
    productTypeId: initial.productTypeId || '',
    categoryPathLabel: initial.categoryPathLabel || '',
    supplierCode: initial.supplierCode || '',
    brandCode: initial.brandCode || '',
    originCountry: initial.originCountry || '',
    ipId: initial.ipId || '',
    ipName: initial.ipName || '',
    ipNameCn: initial.ipNameCn || '',
    productAttributes: normalizeProductAttributes(initial.productAttributes || {}),
    skcList: Array.isArray(initial.skcList) ? initial.skcList : [],
    selectedSizeIds: Array.isArray(initial.selectedSizeIds)
      ? initial.selectedSizeIds
      : deriveSelectedSizeIds(initial.skcList),
    printImages: Array.isArray(initial.printImages) ? initial.printImages : [],
    ipePrintImageNames: Array.isArray(initial.ipePrintImageNames)
      ? initial.ipePrintImageNames
      : printNamesFromSheinPrintImages(initial.printImages),
    sizeChart: initial.sizeChart || {},
    sizeSaleAttributeId: initial.sizeSaleAttributeId || '',
    supplyPrice: initial.supplyPrice || '',
    currency: deriveFormCurrency(initial),
    stockQty: initial.stockQty || '',
    weightGrams: initial.weightGrams || '',
    weightUnit: initial.weightUnit || 'g',
    pkgLength: initial.pkgLength || '10',
    pkgWidth: initial.pkgWidth || '10',
    pkgHeight: initial.pkgHeight || '10',
    dimUnit: initial.dimUnit || 'cm',
    warehouseId: initial.warehouseId || '',
    warehouseName: initial.warehouseName || '',
    selectedSites: Array.isArray(initial.selectedSites) ? initial.selectedSites : [],
    mainSite: initial.mainSite || 'shein',
    shelfWay: initial.shelfWay || '1',
    hopeOnSaleDate: initial.hopeOnSaleDate || '',
  };
}

export default function ProductTemplateEditor({
  templateId: initialTemplateId = '',
  templateScope = 'PERSONAL',
  officialMode = false,
  shopType = '',
  referenceShopId = '',
  afterSaveNavigate,
  onShopTypeChange,
}) {
  const { t } = useTranslation('workbench');
  const navigate = useNavigate();
  const sheinShopCtx = useSheinShopOptional();
  const activeShop = sheinShopCtx?.activeShop ?? null;
  const shopId = referenceShopId || activeShop?.id;
  const shopTemplateQuery = officialMode ? {} : { scope: templateScope };
  const shopCaps = useMemo(
    () => sheinShopCapabilities(officialMode ? shopType : (activeShop?.shopType || shopType)),
    [officialMode, shopType, activeShop?.shopType],
  );
  const officialShopTypeSyncedRef = useRef('');
  const shopTypeRef = useRef(shopType);
  shopTypeRef.current = shopType;
  const onShopTypeChangeRef = useRef(onShopTypeChange);
  onShopTypeChangeRef.current = onShopTypeChange;
  const priceMode = shopCaps.canUpdateSalePrice ? 'sale' : (shopCaps.canUpdateCost ? 'cost' : '');
  const [templateId, setTemplateId] = useState(() => (
    initialTemplateId ? String(initialTemplateId) : ''
  ));
  const [templateMeta, setTemplateMeta] = useState({
    name: '',
    styleNo: '',
    remark: '',
    autoRuleEnabled: false,
    sortOrder: 0,
  });
  const [templateVersion, setTemplateVersion] = useState(null);
  const [versionConflict, setVersionConflict] = useState(false);
  const [reloadingVersion, setReloadingVersion] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(() => Boolean(initialTemplateId));
  const [templateHydrated, setTemplateHydrated] = useState(() => Boolean(!initialTemplateId));
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [activeSection, setActiveSection] = useState('basic');
  const [form, setForm] = useState(() => emptyForm({}));
  const [meta, setMeta] = useState({ categories: null, brands: null, ips: null, sites: null, warehouses: [] });
  const [metaLoading, setMetaLoading] = useState(false);
  /** 半托管/全托管：发布规范 【currency】；自运营：query-site-list 自营站点币种 */
  const [publishCurrencies, setPublishCurrencies] = useState([]);
  const [publishCurrencyLoading, setPublishCurrencyLoading] = useState(false);
  /** 按末级类目加载的发布规范：门控 SKU 件数 / 包装类型 */
  const [skuPkgCaps, setSkuPkgCaps] = useState(EMPTY_SKU_PACKAGE_CAPS);
  const [skuPkgCapsLoading, setSkuPkgCapsLoading] = useState(false);
  /** 关联属性必填规则：attributeId → { allowedValueIds } */
  const [linkedAttrRules, setLinkedAttrRules] = useState(() => new Map());
  const [linkedAttrLoading, setLinkedAttrLoading] = useState(false);
  const [attrTpl, setAttrTpl] = useState(null);
  const [attrLoading, setAttrLoading] = useState(false);
  const [attrError, setAttrError] = useState('');
  const [attrsExpanded, setAttrsExpanded] = useState(false);
  const [attrTemplates, setAttrTemplates] = useState([]);
  const [selectedAttrTemplateId, setSelectedAttrTemplateId] = useState('');
  const [selectedSizeChartTemplateId, setSelectedSizeChartTemplateId] = useState('');
  const [selectedAttrTemplateVersion, setSelectedAttrTemplateVersion] = useState(null);
  const [selectedSizeChartTemplateVersion, setSelectedSizeChartTemplateVersion] = useState(null);

  const handleOfficialShopTypeChange = useCallback((nextType) => {
    const normalized = normalizeSheinShopType(nextType);
    if (!officialMode || !normalized || normalized === shopType) return;
    setForm((prev) => ({
      ...prev,
      categoryId: '',
      productTypeId: '',
      categoryPathLabel: '',
      productAttributes: {},
      selectedSites: [],
      warehouseId: '',
      warehouseName: '',
    }));
    setAttrTpl(null);
    setSelectedAttrTemplateId('');
    setSelectedSizeChartTemplateId('');
    showToast(
      templateId
        ? t('templateEditor.shopTypeSwitched', { shopType: normalized })
        : t('templateEditor.shopTypePicked', { shopType: normalized }),
      'info',
    );
    onShopTypeChange?.(normalized);
  }, [officialMode, shopType, templateId, onShopTypeChange, t]);

  const [refVersionConflicts, setRefVersionConflicts] = useState({});
  const [refChangePreviews, setRefChangePreviews] = useState({});
  const [reloadingRefType, setReloadingRefType] = useState('');
  const [attrTemplateBusy, setAttrTemplateBusy] = useState(false);
  const [attrSaveModalOpen, setAttrSaveModalOpen] = useState(false);
  /** 分类下可自定义属性值的 attributeId 集合 */
  const [customAttrPerm, setCustomAttrPerm] = useState({});
  /** 类目切换后待自动引用的 productTypeId（空=不自动） */
  const pendingAttrAutoForRef = useRef('');
  /** 切类目时暂存旧类目已填参数，待新 schema 就绪后按相似字段回填 */
  const pendingCategoryPreserveRef = useRef(null);
  const attrTemplatesLoadedForRef = useRef('');
  /** 上次属性库自动套用所用的款号（小写）；货号变更时重匹配 */
  const lastAttrAutoStyleKeyRef = useRef(null);
  const attrTplRef = useRef(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [pkgBatchMode, setPkgBatchMode] = useState(null);

  const brandOptions = useMemo(() => flattenBrandRows(meta.brands), [meta.brands]);
  const ipOptions = useMemo(() => flattenIpRows(meta.ips), [meta.ips]);
  const categoryOptions = useMemo(() => flattenLeafCategories(meta.categories), [meta.categories]);
  const siteOptions = useMemo(() => listEnabledSites(meta.sites), [meta.sites]);

  /** 半托管/全托管走发布规范币种；自运营走站点列表（随已选站点对齐） */
  const platformCurrency = useMemo(() => {
    if (shopCaps.usesPriceInfo) {
      return pickSiteListCurrency(meta.sites, {
        preferred: form.currency,
        selectedSites: form.selectedSites,
      });
    }
    return pickPublishCurrency(publishCurrencies, form.currency);
  }, [form.currency, form.selectedSites, meta.sites, publishCurrencies, shopCaps.usesPriceInfo]);

  const applyPublishCurrency = (code) => {
    const next = String(code || '').trim().toUpperCase();
    if (!next) return;
    setForm((prev) => {
      if (String(prev.currency || '').trim().toUpperCase() === next) return prev;
      return {
        ...prev,
        currency: next,
        skcList: (prev.skcList || []).map((skc) => ({
          ...skc,
          skuRows: (skc.skuRows || []).map((sku) => ({
            ...sku,
            currency: next,
          })),
        })),
      };
    });
  };

  const selectedSitesKey = useMemo(
    () => (Array.isArray(form.selectedSites) ? form.selectedSites.map(String).filter(Boolean).join('|') : ''),
    [form.selectedSites],
  );

  // 币种就绪后回写表单：半托管/全托管按发布规范；自运营按站点列表（已选站点优先）。
  useEffect(() => {
    if (!publishCurrencies.length) return;
    const selectedSites = selectedSitesKey ? selectedSitesKey.split('|') : [];
    const next = shopCaps.usesPriceInfo
      ? pickSiteListCurrency(meta.sites, {
        preferred: form.currency,
        selectedSites,
      })
      : pickPublishCurrency(publishCurrencies, form.currency);
    if (!next) return;
    if (String(form.currency || '').trim().toUpperCase() === String(next).trim().toUpperCase()) return;
    applyPublishCurrency(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 规范/站点币种或回填变化时对齐
  }, [publishCurrencies, form.currency, selectedSitesKey, meta.sites, shopCaps.usesPriceInfo]);

  const warehouseOptions = useMemo(() => {
    const rows = Array.isArray(meta.warehouses) ? meta.warehouses : [];
    const mapped = rows
      .map((item) => {
        const id = String(item.id || item.warehouseCode || '');
        const name = String(item.name || item.warehouseName || '').trim();
        return {
          value: id,
          // 下拉只展示仓库名称；提交仍用 value=warehouseCode
          label: name || id,
          name: name || id,
        };
      })
      .filter((item) => item.value);
    if (form.warehouseId && !mapped.some((item) => item.value === String(form.warehouseId))) {
      const fallbackName = String(form.warehouseName || '').trim();
      mapped.unshift({
        value: String(form.warehouseId),
        label: fallbackName || String(form.warehouseId),
        name: fallbackName || String(form.warehouseId),
      });
    }
    return mapped;
  }, [meta.warehouses, form.warehouseId, form.warehouseName]);
  const multiWarehouse = warehouseOptions.length > 1;

  // 库存仓库：默认第一个。编辑回填可能清空 warehouseId，需在列表就绪或选中失效时再默认一次。
  useEffect(() => {
    const rows = Array.isArray(meta.warehouses) ? meta.warehouses : [];
    if (!rows.length) return;
    setForm((prev) => {
      const current = String(prev.warehouseId || '').trim();
      const codes = rows
        .map((item) => String(item.id || item.warehouseCode || '').trim())
        .filter(Boolean);
      if (current && codes.includes(current)) {
        // 补全名称（仅有 code 时）
        if (!String(prev.warehouseName || '').trim()) {
          const hit = rows.find((item) => String(item.id || item.warehouseCode || '') === current);
          const name = hit?.name || hit?.warehouseName || '';
          if (name) return { ...prev, warehouseName: name };
        }
        return prev;
      }
      const first = rows[0];
      const id = String(first.id || first.warehouseCode || '').trim();
      if (!id) return prev;
      return {
        ...prev,
        warehouseId: id,
        warehouseName: first.name || first.warehouseName || '',
      };
    });
  }, [meta.warehouses, form.warehouseId]);
  const colorAttr = attrTpl?.skcSaleAttrs?.[0] || null;
  const sizeAttr = attrTpl?.skuSaleAttrs?.[0] || null;
  const sizeMeasureAttrsBase = attrTpl?.sizeAttrs;
  const sizeMeasureAttrs = useMemo(
    () => (Array.isArray(sizeMeasureAttrsBase) ? sizeMeasureAttrsBase : []).map((attr) => {
      const id = String(attr.attributeId || '');
      if (!id || !linkedAttrRules.has(id)) return attr;
      if (attr.required) return attr;
      return { ...attr, required: true };
    }),
    [sizeMeasureAttrsBase, linkedAttrRules],
  );
  const colorLabel = attributeDisplayLabel(colorAttr) || t('publishSpec.mainSpec');
  const sizeLabel = attributeDisplayLabel(sizeAttr) || t('publishSpec.otherSpec');
  const completion = useMemo(() => calcPublishCompletion(form, attrTpl), [form, attrTpl]);

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev?.[key] && !prev?.form) return prev;
      const next = { ...prev };
      delete next[key];
      delete next.form;
      return next;
    });
  };

  /** 商家货号变更时：若款号为空或仍等于旧货号推导值，则默认同步款号；用户已改过则保留 */
  const onSupplierCodeChange = (value) => {
    const nextCode = String(value || '');
    const prevDerived = styleNoFromSupplierCode(form.supplierCode);
    const nextDerived = styleNoFromSupplierCode(nextCode);
    update('supplierCode', nextCode);
    setTemplateMeta((meta) => {
      const current = String(meta.styleNo || '').trim();
      if (!current || current === prevDerived) {
        return { ...meta, styleNo: nextDerived };
      }
      return meta;
    });
  };

  const setAttrValue = (attributeId, value) => {
    setForm((prev) => ({
      ...prev,
      productAttributes: { ...prev.productAttributes, [attributeId]: value },
    }));
  };

  /** 自定义属性值写入本地属性模板，供下拉即时可选 */
  const appendCustomAttrValue = (attributeId, opt) => {
    if (!attributeId || !opt?.id) return;
    setAttrTpl((prev) => {
      if (!prev) return prev;
      const patchList = (list) => (list || []).map((attr) => {
        if (String(attr.attributeId) !== String(attributeId)) return attr;
        if ((attr.values || []).some((v) => String(v.id) === String(opt.id))) return attr;
        return {
          ...attr,
          values: [...(attr.values || []), {
            id: String(opt.id),
            name: opt.name,
            nameCn: opt.nameCn || opt.name,
          }],
        };
      });
      return {
        ...prev,
        productAttrs: patchList(prev.productAttrs),
        requiredProduct: patchList(prev.requiredProduct),
        mainAttrs: patchList(prev.mainAttrs),
        otherAttrs: patchList(prev.otherAttrs),
        optionalProduct: patchList(prev.optionalProduct),
        skcSaleAttrs: patchList(prev.skcSaleAttrs),
        skuSaleAttrs: patchList(prev.skuSaleAttrs),
      };
    });
  };

  const updateSkc = (skcId, patch) => {
    setForm((prev) => ({
      ...prev,
      skcList: prev.skcList.map((skc) => (skc.id === skcId ? { ...skc, ...patch } : skc)),
    }));
  };

  const updatePrintImages = (imgs) => {
    const list = Array.isArray(imgs) ? imgs : [];
    setForm((prev) => applyPrintNamesToSheinForm({
      ...prev,
      printImages: list,
      ipePrintImageNames: printNamesFromSheinPrintImages(list),
    }));
  };

  const updateSku = (skcId, skuId, patch) => {
    setForm((prev) => ({
      ...prev,
      skcList: prev.skcList.map((skc) => {
        if (skc.id !== skcId) return skc;
        return {
          ...skc,
          skuRows: skc.skuRows.map((sku) => (sku.id === skuId ? { ...sku, ...patch } : sku)),
        };
      }),
    }));
  };

  const batchUpdateSku = (patchOrMapper = {}) => {
    if (typeof patchOrMapper === 'function') {
      setForm((prev) => ({
        ...prev,
        skcList: prev.skcList.map((skc) => ({
          ...skc,
          skuRows: (skc.skuRows || []).map((sku) => {
            const patch = patchOrMapper(skc, sku);
            if (!patch || typeof patch !== 'object') return sku;
            return { ...sku, ...patch };
          }),
        })),
      }));
      showToast(t('templateEditor.skuRuleDone'), 'success');
      return;
    }
    const next = {};
    ['price', 'stock', 'supplierSku', 'weight', 'length', 'width', 'height', 'quantity', 'quantityUnit', 'packageType'].forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(patchOrMapper, key)) next[key] = patchOrMapper[key];
    });
    if (!Object.keys(next).length) return;
    setForm((prev) => ({
      ...prev,
      skcList: prev.skcList.map((skc) => ({
        ...skc,
        skuRows: (skc.skuRows || []).map((sku) => ({ ...sku, ...next })),
      })),
    }));
    showToast(t('publishPkg.appliedAll'), 'success');
  };

  const removeSku = (skcId, skuId) => {
    setForm((prev) => {
      const nextSkcList = prev.skcList.map((skc) => {
        if (skc.id !== skcId) return skc;
        const nextRows = skc.skuRows.filter((sku) => sku.id !== skuId);
        return {
          ...skc,
          skuRows: nextRows.length
            ? nextRows
            : [createSkuRow({ sizeAttributeId: sizeAttr?.attributeId || '' })],
        };
      });
      return {
        ...prev,
        skcList: nextSkcList,
        selectedSizeIds: deriveSelectedSizeIds(nextSkcList),
      };
    });
  };

  const skuDefaultsFromForm = (prev) => ({
    price: prev.supplyPrice || '',
    stock: prev.stockQty || '',
    weight: prev.weightGrams || '',
    currency: prev.currency || '',
  });

  const onColorsChange = (colorKeys) => {
    const platformIds = new Set((colorAttr?.values || []).map((v) => String(v.id)));
    const safeKeys = [...new Set((colorKeys || []).map(String).filter((key) => {
      if (!key) return false;
      if (key.startsWith('custom:')) return Boolean(key.slice('custom:'.length));
      return platformIds.has(key);
    }))];
    setForm((prev) => {
      const defaults = skuDefaultsFromForm(prev);
      const sizeIds = (prev.selectedSizeIds || [])
        .map(String)
        .filter((id) => (sizeAttr?.values || []).some((v) => String(v.id) === id));
      const nextSkcList = safeKeys.map((colorKey) => {
        const isCustom = colorKey.startsWith('custom:');
        let colorExtraValue = '';
        if (isCustom) {
          try {
            colorExtraValue = decodeURIComponent(colorKey.slice('custom:'.length));
          } catch {
            colorExtraValue = colorKey.slice('custom:'.length);
          }
        }
        const existing = prev.skcList.find((skc) => {
          if (isCustom) {
            return Boolean(skc.colorIsCustom || skc.colorExtraValue)
              && String(skc.colorExtraValue || skc.colorLabel || '') === colorExtraValue;
          }
          return String(skc.colorValueId || '') === colorKey && !skc.colorIsCustom;
        });
        const val = !isCustom
          ? colorAttr?.values?.find((v) => String(v.id) === colorKey)
          : null;
        const colorLabel = isCustom
          ? colorExtraValue
          : (val?.nameCn || val?.name || existing?.colorLabel || '');
        if (existing) {
          return {
            ...existing,
            colorAttributeId: colorAttr?.attributeId || existing.colorAttributeId,
            colorValueId: isCustom ? '' : colorKey,
            colorLabel,
            colorCode: existing.colorCode || '',
            colorIsCustom: isCustom,
            colorExtraValue: isCustom ? colorExtraValue : '',
            skuRows: buildSkuRowsForSizes(existing.skuRows, sizeIds, sizeAttr, defaults),
          };
        }
        return createSkc({
          colorAttributeId: colorAttr?.attributeId || '',
          colorValueId: isCustom ? '' : colorKey,
          colorLabel,
          colorCode: '',
          colorIsCustom: isCustom,
          colorExtraValue: isCustom ? colorExtraValue : '',
          skuRows: buildSkuRowsForSizes([], sizeIds, sizeAttr, defaults),
        });
      });
      return { ...prev, skcList: nextSkcList, selectedSizeIds: sizeIds };
    });
  };

  const onSizesChange = (sizeIds) => {
    const allowed = new Set((sizeAttr?.values || []).map((v) => String(v.id)));
    // SHEIN 尺码不支持自定义，仅允许平台属性值
    const safeIds = [...new Set((sizeIds || []).map(String).filter((id) => (
      !isCustomSpecKey(id) && allowed.has(id)
    )))];
    setForm((prev) => {
      const defaults = skuDefaultsFromForm(prev);
      return {
        ...prev,
        selectedSizeIds: safeIds,
        sizeSaleAttributeId: sizeAttr?.attributeId || prev.sizeSaleAttributeId || '',
        skcList: prev.skcList.map((skc) => ({
          ...skc,
          skuRows: buildSkuRowsForSizes(skc.skuRows, safeIds, sizeAttr, defaults),
        })),
      };
    });
  };

  const updateSizeCell = (sizeValueId, attrId, value) => {
    setForm((prev) => ({
      ...prev,
      sizeSaleAttributeId: prev.sizeSaleAttributeId || sizeAttr?.attributeId || '',
      sizeChart: {
        ...prev.sizeChart,
        [sizeValueId]: {
          ...(prev.sizeChart?.[sizeValueId] || {}),
          [attrId]: value,
        },
      },
    }));
  };

  const applySizeChart = (nextChart, options = {}) => {
    const replace = options?.replace === true;
    setForm((prev) => ({
      ...prev,
      sizeSaleAttributeId: prev.sizeSaleAttributeId || sizeAttr?.attributeId || '',
      sizeChart: replace
        ? { ...(nextChart || {}) }
        : {
          ...(prev.sizeChart || {}),
          ...(nextChart || {}),
        },
    }));
  };

  useEffect(() => {
    if (!initialTemplateId) {
      setTemplateLoading(false);
      setTemplateHydrated(true);
      return undefined;
    }
    if (!officialMode && !shopId) {
      return undefined;
    }
    let cancelled = false;
    setTemplateLoading(true);
    setTemplateHydrated(false);
    const loadPromise = officialMode
      ? adminAPI.getOfficialSheinProductTemplate(initialTemplateId).then((res) => res?.item || res?.data?.item || res)
      : sheinPlatformAPI.getProductTemplate(shopId, initialTemplateId, {
        scope: isOfficialProductTemplateScope(templateScope) ? 'OFFICIAL' : templateScope,
      }).then((res) => res?.item || res);
    loadPromise
      .then((item) => {
        if (cancelled || !item) return;
        const rawPayload = item.payload && typeof item.payload === 'object' ? item.payload : {};
        const formData = rawPayload.form && typeof rawPayload.form === 'object'
          ? rawPayload.form
          : rawPayload;
        setTemplateId(String(item.id || initialTemplateId));
        setTemplateVersion(parseTemplateVersion(item.version));
        setVersionConflict(false);
        setTemplateMeta({
          name: String(item.name || '').trim(),
          styleNo: String(item.styleNo || '').trim()
            || styleNoFromSupplierCode(formData.supplierCode || ''),
          remark: String(item.remark || '').trim(),
          autoRuleEnabled: item.autoRuleEnabled === true || item.autoRuleEnabled === 1,
          sortOrder: Number.isFinite(Number(item.sortOrder)) ? Number(item.sortOrder) : 0,
        });
        setForm(sanitizeSheinFormImageSlots(emptyForm({
          ...formData,
          // 表字段为列表/批量更换类目的权威来源，优先于 payload.form 内可能过期的类目
          categoryId: item.categoryId || formData.categoryId || '',
          productTypeId: item.productTypeId || formData.productTypeId || '',
          categoryPathLabel: item.categoryPathLabel || formData.categoryPathLabel || '',
        })));
        setSelectedAttrTemplateId(String(
          rawPayload.selectedAttrTemplateId
          || formData.selectedAttrTemplateId
          || '',
        ).trim());
        setSelectedSizeChartTemplateId(String(
          rawPayload.selectedSizeChartTemplateId
          || formData.selectedSizeChartTemplateId
          || '',
        ).trim());
        setSelectedAttrTemplateVersion(parseTemplateVersion(
          rawPayload.selectedAttrTemplateVersion
          ?? formData.selectedAttrTemplateVersion,
        ));
        setSelectedSizeChartTemplateVersion(parseTemplateVersion(
          rawPayload.selectedSizeChartTemplateVersion
          ?? formData.selectedSizeChartTemplateVersion,
        ));
        setRefVersionConflicts({});
        if (officialMode) {
          const rowType = normalizeSheinShopType(item.shopType);
          if (rowType) {
            const syncKey = `${initialTemplateId}:${rowType}`;
            if (officialShopTypeSyncedRef.current !== syncKey) {
              officialShopTypeSyncedRef.current = syncKey;
              if (rowType !== shopTypeRef.current) {
                onShopTypeChangeRef.current?.(rowType);
              }
            }
          }
        }
        setTemplateHydrated(true);
      })
      .catch((e) => {
        if (cancelled) return;
        showToast(e?.message || t('templateEditor.templateLoadFail'), 'error');
        setTemplateHydrated(true);
        navigate(
          afterSaveNavigate || buildSheinPath('templates/product'),
          { replace: true },
        );
      })
      .finally(() => {
        if (!cancelled) setTemplateLoading(false);
      });
    return () => { cancelled = true; };
  }, [initialTemplateId, shopId, officialMode, templateScope, navigate, afterSaveNavigate, t]);

  // 校验已套用属性库仍属于当前范围；官方复制残留 ID 会 404，解开引用但保留已填写属性
  useEffect(() => {
    if ((!officialMode && !shopId) || !templateHydrated) return undefined;
    const attrId = String(selectedAttrTemplateId || '').trim();
    if (!attrId) return undefined;
    let cancelled = false;
    fetchAttrTemplateDetail(officialMode, shopId, attrId, shopTemplateQuery)
      .then((res) => {
        if (cancelled) return;
        setSelectedAttrTemplateVersion((prev) => (
          prev != null ? prev : parseTemplateVersion(res?.item?.version)
        ));
      })
      .catch((e) => {
        if (cancelled || officialMode || !isMissingTemplateError(e)) return;
        setSelectedAttrTemplateId('');
        setSelectedAttrTemplateVersion(null);
      });
    return () => { cancelled = true; };
  }, [shopId, officialMode, templateHydrated, selectedAttrTemplateId, templateScope]);

  useEffect(() => {
    if ((!officialMode && !shopId) || !templateHydrated) return undefined;
    const sizeId = String(selectedSizeChartTemplateId || '').trim();
    if (!sizeId) return undefined;
    let cancelled = false;
    fetchSizeChartTemplateDetail(officialMode, shopId, sizeId, shopTemplateQuery)
      .then((res) => {
        if (cancelled) return;
        setSelectedSizeChartTemplateVersion((prev) => (
          prev != null ? prev : parseTemplateVersion(res?.item?.version)
        ));
      })
      .catch((e) => {
        if (cancelled || officialMode || !isMissingTemplateError(e)) return;
        setSelectedSizeChartTemplateId('');
        setSelectedSizeChartTemplateVersion(null);
      });
    return () => { cancelled = true; };
  }, [shopId, officialMode, templateHydrated, selectedSizeChartTemplateId, templateScope]);

  useEffect(() => {
    if (officialMode) {
      if (!shopType) {
        setMeta({ categories: null, brands: null, ips: null, sites: null, warehouses: [] });
        setPublishCurrencies([]);
        return undefined;
      }
      let cancelled = false;
      setMetaLoading(true);
      setPublishCurrencyLoading(false);
      Promise.all([
        adminAPI.getOfficialSheinCatalogCategories({ shopType }),
        adminAPI.getOfficialSheinCatalogSites({ shopType }).catch(() => null),
        adminAPI.getOfficialSheinCatalogWarehouses({ shopType }).catch(() => ({ warehouses: [] })),
      ])
        .then(([catRes, siteRes, warehouseRes]) => {
          if (cancelled) return;
          const warehouses = Array.isArray(warehouseRes?.warehouses)
            ? warehouseRes.warehouses
            : [];
          setMeta({
            categories: catRes?.categories ?? null,
            brands: null,
            ips: null,
            sites: siteRes?.siteList ?? siteRes ?? null,
            warehouses,
          });
          setPublishCurrencies([]);
        })
        .catch((e) => {
          if (cancelled) return;
          setMeta({
            categories: null,
            brands: null,
            ips: null,
            sites: null,
            warehouses: [],
          });
          setPublishCurrencies([]);
          showToast(e?.message || t('templateEditor.categoryLoadFail'), 'error');
        })
        .finally(() => {
          if (!cancelled) setMetaLoading(false);
        });
      return () => { cancelled = true; };
    }
    if (!shopId) {
      setMeta({ categories: null, brands: null, ips: null, sites: null, warehouses: [] });
      setPublishCurrencies([]);
      return undefined;
    }
    let cancelled = false;
    setMetaLoading(true);
    setPublishCurrencyLoading(true);
    Promise.all([
      sheinPlatformAPI.getShopCategories(shopId),
      sheinPlatformAPI.getShopBrands(shopId),
      sheinPlatformAPI.getShopIps(shopId),
      sheinPlatformAPI.getShopSiteList(shopId),
      sheinPlatformAPI.getShopWarehouses(shopId).catch(() => ({ warehouses: [] })),
      sheinPlatformAPI.getPublishFillInStandard(shopId, {}).catch(() => null),
    ])
      .then(async ([catRes, brandRes, ipRes, siteRes, warehouseRes, fillInRes]) => {
        if (cancelled) return;
        const warehouses = Array.isArray(warehouseRes?.warehouses)
          ? warehouseRes.warehouses
          : [];
        const loaded = await loadShopPublishCurrencies({
          shopCaps,
          siteList: siteRes?.siteList ?? null,
          fillInStandard: fillInRes,
          refreshSiteList: () => sheinPlatformAPI.getShopSiteList(shopId, { refresh: true }),
        });
        if (cancelled) return;
        setMeta({
          categories: catRes?.categories ?? null,
          brands: brandRes?.brands ?? null,
          ips: ipRes?.ips ?? null,
          sites: loaded.siteList,
          warehouses,
        });
        setPublishCurrencies(loaded.currencies);
        // 供货价币种按末级类目才有，模板阶段还没选类目，别在这里刷「未解析到币种」
        if (loaded.emptyWarning && shopCaps.usesPriceInfo) {
          // eslint-disable-next-line no-console
          console.warn('[shein] publish currency empty', loaded.source, loaded.emptyWarning);
          showToast(loaded.emptyWarning, 'warning');
        }
      })
      .catch((e) => {
        if (!cancelled) showToast(e?.message || t('templateEditor.shopOptionsFail'), 'error');
      })
      .finally(() => {
        if (!cancelled) {
          setMetaLoading(false);
          setPublishCurrencyLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [officialMode, shopId, shopCaps.mode, shopType, t]);

  useEffect(() => {
    if ((!officialMode && !shopId) || !form.categoryId) {
      setAttrTpl(null);
      setAttrError('');
      return undefined;
    }
    if (officialMode && !shopType) {
      setAttrTpl(null);
      setAttrError('');
      return undefined;
    }
    if (!form.productTypeId) {
      setAttrTpl(null);
      setAttrError(t('templateEditor.noProductType'));
      return undefined;
    }
    let cancelled = false;
    setAttrLoading(true);
    setAttrError('');
    fetchCategoryAttributeSchema(officialMode, shopId, form.productTypeId, form.categoryId, {
      shopType,
      categoryPathLabel: form.categoryPathLabel,
    })
      .then((res) => {
        if (cancelled) return;
        const parsed = parseAttributeTemplate(res, { productTypeId: form.productTypeId });
        setAttrTpl(parsed);
        // 材质/成分等控件要 [{valueId,percent}]；编辑回填常是 string / string[]
        // 切类目时按名称把旧类目相似字段回填到新 schema
        const stash = pendingCategoryPreserveRef.current;
        let preserveToast = '';
        if (stash) {
          pendingCategoryPreserveRef.current = null;
          const remappedAttrs = remapProductAttributesAcrossCategories({
            prevAttributes: stash.productAttributes,
            prevAttrTpl: stash.prevAttrTpl,
            nextAttrTpl: parsed,
          });
          const remappedSize = remapSizeChartAcrossCategories({
            prevSizeChart: stash.sizeChart,
            prevSelectedSizeIds: stash.selectedSizeIds,
            prevAttrTpl: stash.prevAttrTpl,
            nextAttrTpl: parsed,
          });
          const remappedSkc = remapSkcListAcrossCategories({
            prevSkcList: stash.skcList,
            prevAttrTpl: stash.prevAttrTpl,
            nextAttrTpl: parsed,
          });
          const selectedSizeIds = remappedSize.selectedSizeIds.length
            ? remappedSize.selectedSizeIds
            : remappedSkc.selectedSizeIds;
          if (
            remappedAttrs.preservedCount > 0
            || remappedSize.preservedCount > 0
            || remappedSkc.preservedCount > 0
          ) {
            pendingAttrAutoForRef.current = '';
            const parts = [];
            if (remappedAttrs.preservedCount > 0) parts.push(t('templateEditor.keptSimilar', { count: remappedAttrs.preservedCount }));
            if (remappedSkc.preservedCount > 0) parts.push(t('templateEditor.keptMainSpec', { count: remappedSkc.preservedCount }));
            if (remappedSize.preservedCount > 0) parts.push(t('templateEditor.sizeChartWord'));
            preserveToast = parts.length ? t('templateEditor.keptPrefix', { detail: parts.join('、') }) : '';
          }
          setForm((prev) => ({
            ...prev,
            productAttributes: alignAttributesToCategoryTemplate(remappedAttrs.attributes, parsed),
            sizeChart: remappedSize.sizeChart,
            selectedSizeIds,
            skcList: remappedSkc.skcList,
          }));
        } else {
          setForm((prev) => {
            const aligned = alignAttributesToCategoryTemplate(prev.productAttributes, parsed);
            if (aligned === prev.productAttributes) return prev;
            return { ...prev, productAttributes: aligned };
          });
        }
        if (preserveToast) showToast(preserveToast, 'success');
        const total = (parsed.productAttrs?.length || 0)
          + (parsed.skcSaleAttrs?.length || 0)
          + (parsed.skuSaleAttrs?.length || 0)
          + (parsed.sizeAttrs?.length || 0);
        if (total === 0) {
          setAttrError(t('templateEditor.attrEmptyCat'));
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setAttrTpl(null);
        setAttrError(e?.message || t('templateEditor.attrLoadFail'));
      })
      .finally(() => {
        if (!cancelled) setAttrLoading(false);
      });
    return () => { cancelled = true; };
  }, [officialMode, shopId, shopType, form.categoryId, form.productTypeId, form.categoryPathLabel, t]);

  useEffect(() => {
    if ((!officialMode && !shopId) || !form.productTypeId) {
      setAttrTemplates([]);
      attrTemplatesLoadedForRef.current = '';
      // 切类目时由 onCategoryChange 清空选中；此处勿清，避免编辑回填被冲掉
      return undefined;
    }
    const productTypeId = String(form.productTypeId);
    let cancelled = false;
    attrTemplatesLoadedForRef.current = '';
    fetchAttrTemplateList(officialMode, shopId, {
      productTypeId,
      page: 1,
      pageSize: 100,
      ...(officialMode ? { shopType } : { scope: templateScope }),
    })
      .then((res) => {
        if (cancelled) return;
        setAttrTemplates(Array.isArray(res?.items) ? res.items : []);
        attrTemplatesLoadedForRef.current = productTypeId;
      })
      .catch(() => {
        if (!cancelled) {
          setAttrTemplates([]);
          attrTemplatesLoadedForRef.current = productTypeId;
        }
      });
    return () => { cancelled = true; };
  }, [shopId, officialMode, shopType, form.productTypeId, templateScope]);

  useEffect(() => {
    if (!shopId || !form.categoryId) {
      setCustomAttrPerm({});
      return undefined;
    }
    let cancelled = false;
    sheinPlatformAPI.getCustomAttributePermission(shopId, {
      category_id_list: [Number(form.categoryId)],
    })
      .then((res) => {
        if (cancelled) return;
        setCustomAttrPerm(parseCustomAttributePermission(res));
      })
      .catch(() => {
        if (!cancelled) setCustomAttrPerm({});
      });
    return () => { cancelled = true; };
  }, [shopId, form.categoryId]);

  // 按末级类目拉取发布规范（quantity_info / package_type 必须带 category_id）
  useEffect(() => {
    if (!form.categoryId) {
      setSkuPkgCaps(EMPTY_SKU_PACKAGE_CAPS);
      setSkuPkgCapsLoading(false);
      return undefined;
    }
    if (!officialMode && !shopId) {
      setSkuPkgCaps(EMPTY_SKU_PACKAGE_CAPS);
      setSkuPkgCapsLoading(false);
      return undefined;
    }
    if (officialMode && !shopType) {
      setSkuPkgCaps(EMPTY_SKU_PACKAGE_CAPS);
      setSkuPkgCapsLoading(false);
      return undefined;
    }
    const categoryId = String(form.categoryId);
    let cancelled = false;
    setSkuPkgCapsLoading(true);
    const fillInPromise = officialMode
      ? adminAPI.getOfficialSheinCatalogPublishFillInStandard({
        shopType,
        category_id: Number(categoryId),
        categoryPathLabel: form.categoryPathLabel || '',
        product_type_id: form.productTypeId || undefined,
      }, { shopId: shopId || undefined })
      : sheinPlatformAPI.getPublishFillInStandard(shopId, {
        category_id: Number(categoryId),
      });
    fillInPromise
      .then((res) => {
        if (cancelled) return;
        const nextCaps = resolveSkuPackageCapabilities(res);
        setSkuPkgCaps(nextCaps);
        // 能力关闭时清空残留，避免脏数据进预检 / 误展示
        if (!nextCaps.quantityInfo.show || !nextCaps.packageType.show) {
          setForm((prev) => {
            let changed = false;
            const skcList = (prev.skcList || []).map((skc) => {
              let skcChanged = false;
              const skuRows = (skc.skuRows || []).map((sku) => {
                const patch = {};
                if (!nextCaps.quantityInfo.show) {
                  if (sku.quantity != null && String(sku.quantity).trim() !== '') {
                    patch.quantity = '';
                  }
                  if (sku.quantityUnit != null && String(sku.quantityUnit) !== '1') {
                    patch.quantityUnit = '1';
                  }
                }
                if (!nextCaps.packageType.show) {
                  if (sku.packageType != null && String(sku.packageType) !== '' && String(sku.packageType) !== '0') {
                    patch.packageType = '';
                  }
                }
                if (!Object.keys(patch).length) return sku;
                skcChanged = true;
                changed = true;
                return { ...sku, ...patch };
              });
              return skcChanged ? { ...skc, skuRows } : skc;
            });
            return changed ? { ...prev, skcList } : prev;
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setSkuPkgCaps(EMPTY_SKU_PACKAGE_CAPS);
      })
      .finally(() => {
        if (!cancelled) setSkuPkgCapsLoading(false);
      });
    return () => { cancelled = true; };
  }, [officialMode, shopId, shopType, form.categoryId, form.categoryPathLabel, form.productTypeId]);

  // 选类目后即查关联必填；已填属性变化时防抖刷新（勿传销售/尺码）
  const productAttrPairsKey = useMemo(
    () => JSON.stringify(collectProductAttributePairs({
      productAttributes: form.productAttributes,
    })),
    [form.productAttributes],
  );
  const linkedAttrCatKeyRef = useRef('');

  useEffect(() => {
    if (!shopId || !form.categoryId || !form.productTypeId) {
      setLinkedAttrRules(new Map());
      setLinkedAttrLoading(false);
      linkedAttrCatKeyRef.current = '';
      return undefined;
    }
    const catKey = `${shopId}|${form.categoryId}|${form.productTypeId}`;
    const categoryChanged = linkedAttrCatKeyRef.current !== catKey;
    if (categoryChanged) {
      linkedAttrCatKeyRef.current = catKey;
      setLinkedAttrRules(new Map());
    }
    const attributeList = collectProductAttributePairs({
      productAttributes: form.productAttributes,
    });
    let cancelled = false;
    setLinkedAttrLoading(true);
    // 类目切换立即查；属性变更防抖，避免连点下拉打爆接口
    const delay = categoryChanged ? 0 : 400;
    const timer = setTimeout(() => {
      sheinPlatformAPI.getAssociatedAttributeRules(shopId, {
        get_linked_rule_req_list: [{
          group_id: '1',
          category_id: Number(form.categoryId),
          product_type_id: Number(form.productTypeId),
          attribute_list: attributeList,
        }],
      })
        .then((res) => {
          if (cancelled) return;
          const nextMap = parseAssociatedAttributeRules(res);
          setLinkedAttrRules(nextMap);
          // 超范围值清空；折叠区内的关联必填自动展开
          setForm((prev) => {
            const attrs = { ...(prev.productAttributes || {}) };
            let changed = false;
            nextMap.forEach((rule, attributeId) => {
              const allowed = rule?.allowedValueIds;
              if (!Array.isArray(allowed) || !allowed.length) return;
              const cur = attrs[attributeId];
              if (cur == null || cur === '') return;
              if (isValueAllowedByLinkedRule(cur, allowed)) return;
              attrs[attributeId] = Array.isArray(cur) ? [] : '';
              changed = true;
            });
            return changed ? { ...prev, productAttributes: attrs } : prev;
          });
        })
        .catch(() => {
          if (!cancelled) setLinkedAttrRules(new Map());
        })
        .finally(() => {
          if (!cancelled) setLinkedAttrLoading(false);
        });
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [shopId, form.categoryId, form.productTypeId, productAttrPairsKey]);

  useEffect(() => {
    if (!colorAttr && !sizeAttr) return;
    setForm((prev) => {
      const colorValues = colorAttr?.values || [];
      const sizeValues = sizeAttr?.values || [];
      const colorIds = new Set(colorValues.map((v) => String(v.id)));
      const sizeIds = new Set(sizeValues.map((v) => String(v.id)));
      const defaults = {
        price: prev.supplyPrice || '',
        stock: prev.stockQty || '',
        weight: prev.weightGrams || '',
        currency: prev.currency || '',
      };
      // 尺码表纵轴以 SKU 规格为准，禁止合并模板残留的 selectedSizeIds（否则会出现两个 mid-platform）
      const rowSizeIds = deriveSelectedSizeIds(prev.skcList || []).map(String);
      const prevSizeIds = (prev.selectedSizeIds || []).map(String);
      const nextSizeIds = rowSizeIds.length
        ? rowSizeIds
        : prevSizeIds.filter((id) => !isCustomSpecKey(id) && (!sizeValues.length || sizeIds.has(id)));
      const nextSkcList = (prev.skcList || []).map((skc) => {
        const knownColor = !skc.colorValueId || colorIds.has(String(skc.colorValueId));
        const asCustom = Boolean(
          skc.colorIsCustom
          || skc.colorExtraValue
          || (colorValues.length > 0 && skc.colorValueId && !knownColor),
        );
        const colorLabel = asCustom
          ? (skc.colorExtraValue || skc.colorLabel || '')
          : (colorAttr?.values?.find((v) => String(v.id) === String(skc.colorValueId))?.name
            || skc.colorLabel);
        const existingRows = skc.skuRows || [];
        // 编辑多 SKU：保留全部行；仅在新建且用户主动勾选尺码时按 sizeIds 重建
        const keepAllRows = existingRows.length > 1 || !nextSizeIds.length;
        return {
          ...skc,
          colorAttributeId: skc.colorAttributeId || colorAttr?.attributeId || '',
          colorIsCustom: asCustom,
          colorExtraValue: asCustom
            ? (skc.colorExtraValue || skc.colorLabel || '')
            : (skc.colorExtraValue || ''),
          colorLabel,
          skuRows: keepAllRows
            ? existingRows.map((row) => ({
              ...row,
              sizeAttributeId: row.sizeAttributeId || sizeAttr?.attributeId || '',
            }))
            : buildSkuRowsForSizes(existingRows, nextSizeIds, sizeAttr, defaults),
        };
      }).filter((skc) => (
        skc.colorValueId
        || String(skc.colorExtraValue || '').trim()
        || String(skc.colorLabel || '').trim()
        || (skc.skuRows || []).length > 0
      ));
      const nextState = {
        ...prev,
        sizeSaleAttributeId: prev.sizeSaleAttributeId || sizeAttr?.attributeId || '',
        selectedSizeIds: nextSizeIds.length ? nextSizeIds : prev.selectedSizeIds,
        skcList: nextSkcList,
      };
      const sameSizes = JSON.stringify((prev.selectedSizeIds || []).map(String))
        === JSON.stringify((nextState.selectedSizeIds || []).map(String));
      const sameSaleAttr = String(prev.sizeSaleAttributeId || '') === String(nextState.sizeSaleAttributeId || '');
      const sameSkc = prev.skcList === nextSkcList || (
        (prev.skcList || []).length === nextSkcList.length
        && (prev.skcList || []).every((skc, i) => {
          const n = nextSkcList[i];
          return skc === n
            || (
              String(skc.colorAttributeId || '') === String(n.colorAttributeId || '')
              && Boolean(skc.colorIsCustom) === Boolean(n.colorIsCustom)
              && String(skc.colorExtraValue || '') === String(n.colorExtraValue || '')
              && String(skc.colorLabel || '') === String(n.colorLabel || '')
              && (skc.skuRows || []).length === (n.skuRows || []).length
              && (skc.skuRows || []).every((row, j) => (
                String(row.sizeAttributeId || '') === String(n.skuRows[j]?.sizeAttributeId || '')
              ))
            );
        })
      );
      if (sameSizes && sameSaleAttr && sameSkc) return prev;
      return nextState;
    });
  }, [colorAttr?.attributeId, sizeAttr?.attributeId]);

  useEffect(() => {
    if (!form.categoryId || !categoryOptions.length) return;
    const selected = categoryOptions.find((item) => String(item.categoryId) === String(form.categoryId));
    if (!selected) return;
    const needType = !form.productTypeId && selected.productTypeId;
    const needPath = !form.categoryPathLabel && selected.label;
    if (!needType && !needPath) return;
    setForm((prev) => {
      if (String(prev.categoryId) !== String(selected.categoryId)) return prev;
      return {
        ...prev,
        productTypeId: prev.productTypeId || (selected.productTypeId ? String(selected.productTypeId) : ''),
        categoryPathLabel: prev.categoryPathLabel || selected.label || '',
      };
    });
  }, [form.categoryId, form.productTypeId, form.categoryPathLabel, categoryOptions]);

  const onCategoryChange = (categoryId) => {
    const nextCategoryId = categoryId == null || categoryId === '' ? '' : String(categoryId);
    if (String(form.categoryId || '') === nextCategoryId) {
      return;
    }
    const selected = categoryOptions.find((item) => String(item.categoryId) === nextCategoryId);
    const nextProductTypeId = selected?.productTypeId ? String(selected.productTypeId) : '';
    pendingCategoryPreserveRef.current = {
      productAttributes: pickFilledProductAttributes(form.productAttributes),
      sizeChart: form.sizeChart && typeof form.sizeChart === 'object' ? form.sizeChart : {},
      selectedSizeIds: Array.isArray(form.selectedSizeIds) ? [...form.selectedSizeIds] : [],
      skcList: Array.isArray(form.skcList) ? form.skcList : [],
      prevAttrTpl: attrTplRef.current,
    };
    pendingAttrAutoForRef.current = nextProductTypeId;
    setSelectedAttrTemplateId('');
    setSelectedSizeChartTemplateId('');
    setSelectedAttrTemplateVersion(null);
    setSelectedSizeChartTemplateVersion(null);
    setRefVersionConflicts({});
    setRefChangePreviews({});
    setForm((prev) => ({
      ...prev,
      categoryId: nextCategoryId,
      productTypeId: nextProductTypeId,
      categoryPathLabel: selected?.label || '',
      productAttributes: {},
      sizeChart: {},
      skcList: [],
      selectedSizeIds: [],
    }));
    setAttrsExpanded(false);
    if (!templateId && nextProductTypeId && (officialMode ? shopType : shopId)) {
      const loadPriority = officialMode
        ? adminAPI.listOfficialSheinProductTemplates({
          productTypeId: nextProductTypeId,
          shopType,
          page: 1,
          pageSize: 100,
        })
        : sheinPlatformAPI.listProductTemplates(shopId, {
          productTypeId: nextProductTypeId,
          page: 1,
          pageSize: 100,
        });
      loadPriority.then((res) => {
        setTemplateMeta((prev) => ({
          ...prev,
          sortOrder: nextTemplatePriority(res?.items),
        }));
      }).catch(() => {
        /* ignore */
      });
    }
  };

  const onIpChange = (ipId) => {
    const selected = ipOptions.find((item) => item.ipId === ipId);
    setForm((prev) => ({
      ...prev,
      ipId: selected?.ipId || '',
      ipName: selected?.ipName || '',
      ipNameCn: selected?.ipNameCn || '',
    }));
  };

  const onSitesChange = (abbrs) => {
    const selected = [...new Set((abbrs || []).map(String).filter(Boolean))];
    const first = siteOptions.find((s) => selected.includes(s.siteAbbr));
    setForm((prev) => ({
      ...prev,
      selectedSites: selected,
      mainSite: first?.mainSiteCode || prev.mainSite || 'shein',
      // 自运营币种随已选站点由 pickSiteListCurrency 回写；半托管/全托管仍走发布规范
    }));
  };

  const onWarehouseChange = (warehouseId) => {
    const selected = warehouseOptions.find((item) => String(item.value) === String(warehouseId));
    setForm((prev) => ({
      ...prev,
      warehouseId: selected?.value || '',
      warehouseName: selected?.name || '',
    }));
  };

  const scrollToSection = (id) => {
    setActiveSection(id);
    document.getElementById(`spo-pub-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const buildPublishFormSnapshot = (formSource = form) => {
    const sizeValueLabels = {};
    (sizeAttr?.values || []).forEach((v) => {
      if (v?.id != null && v?.name) sizeValueLabels[String(v.id)] = String(v.name);
    });
    const skcList = (formSource.skcList || []).map((skc) => ({
      ...skc,
      skuRows: (skc.skuRows || []).map((sku) => ({
        ...sku,
        sizeLabel: sizeAttr?.values?.find((v) => String(v.id) === String(sku.sizeValueId))?.name
          || sku.sizeLabel
          || '',
      })),
    }));
    const selectedSizeIds = (formSource.selectedSizeIds?.length
      ? formSource.selectedSizeIds
      : deriveSelectedSizeIds(skcList)
    ).map((id) => String(id));
    return {
      ...formSource,
      shopType: formSource.shopType || activeShop?.shopType || '',
      skcList,
      selectedSizeIds,
      sizeValueLabels,
      selectedAttrTemplateId: String(selectedAttrTemplateId || '').trim(),
      selectedSizeChartTemplateId: String(selectedSizeChartTemplateId || '').trim(),
    };
  };

  const reloadTemplateFromServer = async () => {
    if (!shopId || !templateId) return;
    setReloadingVersion(true);
    try {
      const res = await sheinPlatformAPI.getProductTemplate(shopId, templateId, {
        scope: isOfficialProductTemplateScope(templateScope) ? 'OFFICIAL' : templateScope,
      });
      const item = res?.item || res || {};
      const rawPayload = item.payload && typeof item.payload === 'object' ? item.payload : {};
      const formData = rawPayload.form && typeof rawPayload.form === 'object'
        ? rawPayload.form
        : rawPayload;
      setTemplateVersion(parseTemplateVersion(item.version));
      setTemplateMeta({
        name: String(item.name || '').trim(),
        styleNo: String(item.styleNo || '').trim()
          || styleNoFromSupplierCode(formData.supplierCode || ''),
        remark: String(item.remark || '').trim(),
        autoRuleEnabled: item.autoRuleEnabled === true || item.autoRuleEnabled === 1,
        sortOrder: Number.isFinite(Number(item.sortOrder)) ? Number(item.sortOrder) : 0,
      });
      setForm(sanitizeSheinFormImageSlots(emptyForm({
        ...formData,
        // 表字段为列表/批量更换类目的权威来源，优先于 payload.form 内可能过期的类目
        categoryId: item.categoryId || formData.categoryId || '',
        productTypeId: item.productTypeId || formData.productTypeId || '',
        categoryPathLabel: item.categoryPathLabel || formData.categoryPathLabel || '',
      })));
      setSelectedAttrTemplateId(String(
        rawPayload.selectedAttrTemplateId
        || formData.selectedAttrTemplateId
        || '',
      ).trim());
      setSelectedSizeChartTemplateId(String(
        rawPayload.selectedSizeChartTemplateId
        || formData.selectedSizeChartTemplateId
        || '',
      ).trim());
      setSelectedAttrTemplateVersion(parseTemplateVersion(
        rawPayload.selectedAttrTemplateVersion
        ?? formData.selectedAttrTemplateVersion,
      ));
      setSelectedSizeChartTemplateVersion(parseTemplateVersion(
        rawPayload.selectedSizeChartTemplateVersion
        ?? formData.selectedSizeChartTemplateVersion,
      ));
      setRefVersionConflicts({});
      setVersionConflict(false);
      showToast(t('templateEditor.reloaded'), 'success');
    } catch (e) {
      showToast(e?.message || t('templateEditor.reloadFail'), 'error');
    } finally {
      setReloadingVersion(false);
    }
  };

  const clearRefVersionConflict = (type) => {
    const key = conflictFieldKey(type);
    setRefVersionConflicts((prev) => {
      if (!prev?.[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setRefChangePreviews((prev) => {
      if (!prev?.[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const buildAttrChangePreview = async () => {
    const id = String(selectedAttrTemplateId || '').trim();
    if ((!officialMode && !shopId) || !id) {
      return { title: t('templateEditor.attrUpdated'), changes: [] };
    }
    const res = await fetchAttrTemplateDetail(officialMode, shopId, id, shopTemplateQuery);
    const item = res?.item || {};
    const payload = item.payload || {};
    const { patch } = applyProductAttributeTemplatePayload(
      payload,
      (allProductAttrs || []).map((attr) => String(attr.attributeId || '')).filter(Boolean),
    );
    const nextAttrs = alignAttributesToCategoryTemplate(
      { ...patch },
      attrTplRef.current || attrTpl,
    );
    const { changes, count } = diffProductAttributeChanges({
      current: form.productAttributes || {},
      next: nextAttrs,
      attrDefs: [
        ...(attrTpl?.requiredProduct || []),
        ...(attrTpl?.mainAttrs || []),
        ...(attrTpl?.otherAttrs || []),
      ],
    });
    return {
      title: t('templateEditor.attrUpdated'),
      intro: t('templateEditor.attrDiffIntro', { name: item.name || id, count }),
      changes,
      count,
      templateName: item.name || id,
    };
  };

  const buildSizeChartChangePreview = async () => {
    const id = String(selectedSizeChartTemplateId || '').trim();
    if ((!officialMode && !shopId) || !id) {
      return { title: t('templateEditor.sizeUpdated'), changes: [] };
    }
    const res = await fetchSizeChartTemplateDetail(officialMode, shopId, id, shopTemplateQuery);
    const item = res?.item || {};
    const patch = extractSizeChartTemplatePatch(item);
    const { changes, count } = diffSizeChartChanges({
      current: form.sizeChart || {},
      next: patch.sizeChart || {},
      measureAttrs: sizeMeasureAttrs,
    });
    return {
      title: t('templateEditor.sizeUpdated'),
      intro: t('templateEditor.sizeDiffIntro', { name: item.name || id, count }),
      changes,
      count,
      templateName: item.name || id,
    };
  };

  const reloadReferencedTemplateRegion = async (type) => {
    if (!officialMode && !shopId) return;
    setReloadingRefType(type);
    try {
      if (type === TEMPLATE_VERSION_TYPES.attribute) {
        const id = String(selectedAttrTemplateId || '').trim();
        if (!id) return;
        const res = await fetchAttrTemplateDetail(officialMode, shopId, id, shopTemplateQuery);
        const item = res?.item || {};
        const patch = extractAttrTemplatePatch(item);
        setForm((prev) => {
          const aligned = alignAttributesToCategoryTemplate(
            { ...(patch.productAttributes || {}) },
            attrTplRef.current || attrTpl,
          );
          return { ...prev, productAttributes: aligned };
        });
        setSelectedAttrTemplateVersion(patch.version);
        clearRefVersionConflict(type);
        showToast(t('templateEditor.reloadedAttrs'), 'success');
        return;
      }
      if (type === TEMPLATE_VERSION_TYPES.sizeChart) {
        const id = String(selectedSizeChartTemplateId || '').trim();
        if (!id) return;
        const res = await fetchSizeChartTemplateDetail(officialMode, shopId, id, shopTemplateQuery);
        const patch = extractSizeChartTemplatePatch(res?.item || {});
        applySizeChart(patch.sizeChart || {}, { replace: true });
        setSelectedSizeChartTemplateVersion(patch.version);
        clearRefVersionConflict(type);
        showToast(t('templateEditor.reloadedSize'), 'success');
      }
    } catch (e) {
      if (!officialMode && isMissingTemplateError(e)) {
        if (type === TEMPLATE_VERSION_TYPES.attribute) {
          setSelectedAttrTemplateId('');
          setSelectedAttrTemplateVersion(null);
        } else if (type === TEMPLATE_VERSION_TYPES.sizeChart) {
          setSelectedSizeChartTemplateId('');
          setSelectedSizeChartTemplateVersion(null);
        }
        clearRefVersionConflict(type);
        showToast(
          type === TEMPLATE_VERSION_TYPES.sizeChart
            ? t('templateEditor.refSizeOut')
            : t('templateEditor.refAttrOut'),
          'warning',
        );
        return;
      }
      showToast(e?.message || t('templateEditor.reloadFail'), 'error');
    } finally {
      setReloadingRefType('');
    }
  };

  const onSaveProductTemplate = async () => {
    if (!officialMode && !shopId) {
      showToast(t('templateEditor.pickShopSave'), 'warning');
      return;
    }
    if (officialMode && !shopType) {
      showToast(t('templateEditor.pickShopTypeShared'), 'warning');
      return;
    }
    const name = String(templateMeta.name || '').trim();
    if (!name) {
      showToast(t('templateEditor.fillName'), 'error');
      setActiveSection('basic');
      return;
    }
    const styleNo = String(templateMeta.styleNo || '').trim().slice(0, 128);
    if (!styleNo) {
      showToast(t('templateEditor.fillStyle'), 'error');
      setActiveSection('basic');
      return;
    }
    if (!officialMode && (!form.categoryId || !form.productTypeId)) {
      showToast(t('templateEditor.pickCategory'), 'error');
      setActiveSection('basic');
      return;
    }
    setSavingTemplate(true);
    try {
      let nextAttrTemplateId = String(selectedAttrTemplateId || '').trim();
      let nextAttrTemplateVersion = selectedAttrTemplateVersion;
      let nextSizeChartTemplateId = String(selectedSizeChartTemplateId || '').trim();
      let nextSizeChartTemplateVersion = selectedSizeChartTemplateVersion;
      if (!officialMode && shopId) {
        // 保存前校验已套用的属性库 / 尺码库版本，避免把脏引用写进产品模板
        const refCheck = await checkTemplateVersions(
          sheinPlatformAPI,
          shopId,
          collectAppliedTemplateRefs({
            attrTemplateId: nextAttrTemplateId,
            attrTemplateVersion: nextAttrTemplateVersion,
            sizeChartTemplateId: nextSizeChartTemplateId,
            sizeChartTemplateVersion: nextSizeChartTemplateVersion,
          }).filter((ref) => ref.version != null),
        );
        if (!refCheck.ok) {
          const conflicts = refCheck.conflicts || [];
          const { missingLibraryRefs, blocking } = partitionTemplateVersionConflicts(conflicts);
          if (missingLibraryRefs.length) {
            missingLibraryRefs.forEach((c) => {
              if (c.type === TEMPLATE_VERSION_TYPES.attribute) {
                nextAttrTemplateId = '';
                nextAttrTemplateVersion = null;
                setSelectedAttrTemplateId('');
                setSelectedAttrTemplateVersion(null);
              } else if (c.type === TEMPLATE_VERSION_TYPES.sizeChart) {
                nextSizeChartTemplateId = '';
                nextSizeChartTemplateVersion = null;
                setSelectedSizeChartTemplateId('');
                setSelectedSizeChartTemplateVersion(null);
              }
            });
          }
          if (blocking.length) {
            const nextMarks = {};
            blocking.forEach((c) => {
              const key = conflictFieldKey(c.type);
              if (key) nextMarks[key] = true;
            });
            setRefVersionConflicts(nextMarks);
            const nextPreviews = {};
            await Promise.all(blocking.map(async (c) => {
              try {
                if (c.type === TEMPLATE_VERSION_TYPES.attribute) {
                  const preview = await buildAttrChangePreview();
                  nextPreviews.productAttributes = preview;
                } else if (c.type === TEMPLATE_VERSION_TYPES.sizeChart) {
                  const preview = await buildSizeChartChangePreview();
                  nextPreviews.sizeChart = preview;
                }
              } catch {
                /* ignore preview errors */
              }
            }));
            setRefChangePreviews(nextPreviews);
            const attrCount = nextPreviews.productAttributes?.count;
            const sizeCount = nextPreviews.sizeChart?.count;
            const detailBits = [];
            if (attrCount != null) detailBits.push(t('templateEditor.attrCount', { count: attrCount }));
            if (sizeCount != null) detailBits.push(t('templateEditor.sizeCount', { count: sizeCount }));
            showToast(
              detailBits.length
                ? `${conflictToastMessage(blocking)}${t('templateEditor.conflictDetail', { detail: detailBits.join('，') })}`
                : conflictToastMessage(blocking),
              'warning',
              3000,
            );
            const firstType = blocking[0]?.type;
            if (firstType === TEMPLATE_VERSION_TYPES.sizeChart) {
              setActiveSection('size');
            } else {
              setActiveSection('attrs');
            }
            const firstField = conflictFieldKey(firstType) || 'productAttributes';
            const el = document.querySelector(`[data-field-error="${firstField}"]`);
            if (el?.scrollIntoView) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
          }
        }
        setRefVersionConflicts({});
        setRefChangePreviews({});
      }

      if (!officialMode) {
        const supplierCode = String(form.supplierCode || '').trim();
        if (supplierCode && !supplierCode.includes('-')) {
          showToast(t('templateEditor.skuHyphen'), 'error');
          setActiveSection('basic');
          return;
        }
        const badSku = (form.skcList || []).flatMap((skc) => (skc.skuRows || []).map((sku) => ({
          skc,
          sku,
        }))).find(({ sku }) => {
          const v = String(sku?.supplierSku || '').trim();
          return v && !v.includes('-');
        });
        if (badSku) {
          showToast(t('templateEditor.skuHyphenRow'), 'error');
          setActiveSection('spec');
          return;
        }
      }

      // 有类目时须等属性模板就绪，再按当前类目剔除旧残留属性后落库
      if (form.productTypeId && (attrLoading || !attrTpl)) {
        showToast(t('templateEditor.attrsNotReady'), 'warning');
        setActiveSection('attrs');
        return;
      }

      const formWithPrint = officialMode
        ? form
        : applyPrintNamesToSheinForm(await enrichSheinFormPrintNames(form));
      const cleanedAttributes = attrTpl
        ? filterProductAttributesToCategory(formWithPrint.productAttributes, attrTpl)
        : (formWithPrint.productAttributes || {});
      const formToPersist = {
        ...formWithPrint,
        productAttributes: cleanedAttributes,
      };
      if (formWithPrint !== form || cleanedAttributes !== formWithPrint.productAttributes) {
        setForm(formToPersist);
      }

      const body = {
        styleNo,
        name,
        remark: String(templateMeta.remark || '').trim() || undefined,
        productTypeId: formToPersist.productTypeId,
        categoryId: formToPersist.categoryId,
        categoryPathLabel: formToPersist.categoryPathLabel || undefined,
        autoRuleEnabled: !!templateMeta.autoRuleEnabled,
        sortOrder: Number.isFinite(Number(templateMeta.sortOrder)) ? Number(templateMeta.sortOrder) : 0,
        payload: {
          // 落库前剥掉 blob:/data:，只留 canonical /uploads/…，隔日打开才不会灰块
          form: persistSheinFormImageSlots({
            ...buildPublishFormSnapshot(formToPersist),
            selectedAttrTemplateId: nextAttrTemplateId,
            selectedSizeChartTemplateId: nextSizeChartTemplateId,
          }),
          selectedAttrTemplateId: nextAttrTemplateId,
          selectedSizeChartTemplateId: nextSizeChartTemplateId,
          selectedAttrTemplateVersion: nextAttrTemplateVersion,
          selectedSizeChartTemplateVersion: nextSizeChartTemplateVersion,
        },
      };
      if (officialMode && shopType) {
        body.shopType = shopType;
      }
      if (templateId && templateVersion != null) {
        body.version = templateVersion;
      }
      if (!templateId && !officialMode) {
        body.scope = templateScope;
      }
      const res = officialMode
        ? (templateId
          ? await adminAPI.updateOfficialSheinProductTemplate(templateId, body)
          : await adminAPI.createOfficialSheinProductTemplate(body))
        : (templateId
          ? await sheinPlatformAPI.updateProductTemplate(shopId, templateId, body)
          : await sheinPlatformAPI.createProductTemplate(shopId, body));
      const savedItem = officialMode
        ? (res?.item || res?.data?.item || res)
        : (res?.item || res);
      if (savedItem?.id) setTemplateId(String(savedItem.id));
      if (savedItem && parseTemplateVersion(savedItem.version) != null) {
        setTemplateVersion(parseTemplateVersion(savedItem.version));
      }
      setVersionConflict(false);
      setTemplateMeta((prev) => ({ ...prev, styleNo }));
      showToast(templateId ? t('templateEditor.saved') : t('templateEditor.created'), 'success');
      navigate(officialMode && afterSaveNavigate ? afterSaveNavigate : buildSheinPath('templates/product'));
    } catch (e) {
      if (isTemplateVersionConflictError(e)) {
        showToast(conflictToastMessage([{ type: 'product' }]), 'warning', 3000);
        setVersionConflict(true);
        setActiveSection('basic');
      } else {
        showToast(e?.message || t('templateEditor.saveFail'), 'error');
      }
    } finally {
      setSavingTemplate(false);
    }
  };

  const onPublishFromTemplate = async () => {
    if (!shopId) {
      showToast(t('templateEditor.pickShopPublish'), 'warning');
      return;
    }
    try {
      const formWithPrint = applyPrintNamesToSheinForm(await enrichSheinFormPrintNames(form));
      if (formWithPrint !== form) {
        setForm(formWithPrint);
      }
      const token = await writeProductTemplatePublishHandoff(
        persistSheinFormImageSlots(buildPublishFormSnapshot(formWithPrint)),
      );
      if (!token) {
        if (templateId) {
          showToast(t('templateEditor.passFail'), 'warning');
          openSheinPathInNewTab(
            `products/publish?productTemplateId=${encodeURIComponent(templateId)}`,
          );
          return;
        }
        showToast(t('templateEditor.passNeedSave'), 'error');
        return;
      }
      openSheinPathInNewTab(
        `products/publish?fromProductTemplate=${encodeURIComponent(token)}`,
      );
    } catch (e) {
      showToast(e?.message || t('templateEditor.openPublishFail'), 'error');
    }
  };

  const showSkuQuantity = !skuPkgCapsLoading && skuPkgCaps.quantityInfo.show === true;
  const showSkuPackageType = !skuPkgCapsLoading && skuPkgCaps.packageType.show === true;
  const requireSkuQuantity = showSkuQuantity && skuPkgCaps.quantityInfo.required === true;
  const requireSkuPackageType = showSkuPackageType && skuPkgCaps.packageType.required === true;

  const isLinkedRequired = (attributeId) => linkedAttrRules.has(String(attributeId || ''));
  const linkedAllowedIds = (attributeId) => {
    const rule = linkedAttrRules.get(String(attributeId || ''));
    return Array.isArray(rule?.allowedValueIds) ? rule.allowedValueIds : null;
  };

  const { requiredAttrs, optionalAttrs } = useMemo(
    () => partitionProductAttrsForDisplay(attrTpl, linkedAttrRules),
    [attrTpl, linkedAttrRules],
  );
  const allProductAttrs = useMemo(
    () => [...requiredAttrs, ...optionalAttrs],
    [requiredAttrs, optionalAttrs],
  );
  const allowedAttrIds = useMemo(
    () => allProductAttrs.map((attr) => String(attr.attributeId || '')).filter(Boolean),
    [allProductAttrs],
  );
  attrTplRef.current = attrTpl;

  const resolvedAttrStyleNo = useMemo(
    () => styleNoFromSupplierCode(
      templateMeta.styleNo || form.supplierCode,
      attrTemplates.map((item) => item?.styleNo).filter(Boolean),
    ),
    [templateMeta.styleNo, form.supplierCode, attrTemplates],
  );

  useEffect(() => {
    const target = String(form.productTypeId || '');
    if (!target || attrLoading || !allowedAttrIds.length) return undefined;
    if (attrTemplatesLoadedForRef.current !== target) return undefined;

    const styleKey = String(resolvedAttrStyleNo || '').trim().toLowerCase();
    const pendingCategory = String(pendingAttrAutoForRef.current || '') === target;
    if (lastAttrAutoStyleKeyRef.current === null) {
      lastAttrAutoStyleKeyRef.current = styleKey;
      if (!pendingCategory) return undefined;
    }
    const styleChanged = styleKey !== lastAttrAutoStyleKeyRef.current;
    if (!pendingCategory && !styleChanged) return undefined;

    const picked = pickAutoRuleTemplate(attrTemplates, { styleNo: resolvedAttrStyleNo });
    pendingAttrAutoForRef.current = '';
    lastAttrAutoStyleKeyRef.current = styleKey;
    if (!picked?.id) return undefined;

    let cancelled = false;
    setSelectedAttrTemplateId(String(picked.id));
    setAttrTemplateBusy(true);
    fetchAttrTemplateDetail(officialMode, shopId, picked.id, shopTemplateQuery)
      .then((res) => {
        if (cancelled) return;
        const item = res?.item || {};
        const payload = item.payload || {};
        const { patch, filled } = applyProductAttributeTemplatePayload(payload, allowedAttrIds);
        setForm((prev) => {
          if (String(prev.productTypeId) !== target) return prev;
          // 整表替换：空模板则清空类目属性
          const aligned = alignAttributesToCategoryTemplate({ ...patch }, attrTplRef.current);
          return { ...prev, productAttributes: aligned };
        });
        setSelectedAttrTemplateVersion(parseTemplateVersion(item.version));
        const styleHint = resolvedAttrStyleNo ? t('templateEditor.styleHint', { styleNo: resolvedAttrStyleNo }) : '';
        showToast(
          filled
            ? t('templateEditor.autoNamed', { name: picked.name || picked.id, hint: styleHint })
            : t('templateEditor.autoEmpty'),
          'success',
        );
      })
      .catch(() => {
        /* 自动套用失败不打断发品 */
      })
      .finally(() => {
        if (!cancelled) setAttrTemplateBusy(false);
      });
    return () => { cancelled = true; };
  }, [
    shopId,
    officialMode,
    form.productTypeId,
    resolvedAttrStyleNo,
    attrLoading,
    allowedAttrIds,
    attrTemplates,
    templateScope,
    t,
  ]);

  const applyAttrTemplate = async (templateId) => {
    const id = String(templateId || '').trim();
    if ((!officialMode && !shopId) || !id) return;
    if (!allowedAttrIds.length) {
      showToast(t('templateEditor.attrsNotLoaded'), 'error');
      return;
    }
    setAttrTemplateBusy(true);
    try {
      const res = await fetchAttrTemplateDetail(officialMode, shopId, id, shopTemplateQuery);
      const item = res?.item || {};
      const payload = item.payload || {};
      const { patch, filled } = applyProductAttributeTemplatePayload(payload, allowedAttrIds);
      setForm((prev) => {
        // 整表替换：空模板则清空类目属性
        const aligned = alignAttributesToCategoryTemplate({ ...patch }, attrTplRef.current || attrTpl);
        return { ...prev, productAttributes: aligned };
      });
      setSelectedAttrTemplateVersion(parseTemplateVersion(item.version));
      clearRefVersionConflict(TEMPLATE_VERSION_TYPES.attribute);
      showToast(filled ? t('templateEditor.attrApplied', { count: filled }) : t('templateEditor.attrEmptyCleared'), 'success');
    } catch (e) {
      showToast(e?.message || t('templateEditor.attrApplyFail'), 'error');
    } finally {
      setAttrTemplateBusy(false);
    }
  };

  const onAttrTemplateSelect = (templateId) => {
    const id = String(templateId || '').trim();
    setSelectedAttrTemplateId(id);
    if (id) {
      applyAttrTemplate(id);
      return;
    }
    setSelectedAttrTemplateVersion(null);
    clearRefVersionConflict(TEMPLATE_VERSION_TYPES.attribute);
    // 下拉选空：直接清除类目属性
    setForm((prev) => ({ ...prev, productAttributes: {} }));
  };

  const openAttrSaveModal = () => {
    if (!officialMode && !shopId) {
      showToast(t('returnList.pickShop'), 'error');
      return;
    }
    if (!form.categoryId || !form.productTypeId) {
      showToast(t('templateEditor.pickCategory'), 'error');
      return;
    }
    if (!countFilledProductAttributes(form.productAttributes)) {
      showToast(t('templateEditor.fillAttrsFirst'), 'error');
      return;
    }
    setAttrSaveModalOpen(true);
  };

  const refreshAttrTemplates = async () => {
    if (!form.productTypeId || (!officialMode && !shopId)) return;
    try {
      const listRes = await fetchAttrTemplateList(officialMode, shopId, {
        productTypeId: form.productTypeId,
        page: 1,
        pageSize: 50,
        ...(officialMode ? { shopType } : { scope: templateScope }),
      });
      setAttrTemplates(Array.isArray(listRes?.items) ? listRes.items : []);
    } catch {
      /* ignore */
    }
  };

  const visibleOptional = useMemo(() => {
    if (attrsExpanded) return optionalAttrs;
    const hintIds = new Set(Object.keys(indexAttributeChangeHints(
      refChangePreviews.productAttributes,
      !!refVersionConflicts.productAttributes,
    )));
    const head = optionalAttrs.slice(0, 9);
    const headIds = new Set(head.map((a) => String(a.attributeId)));
    const extraTail = optionalAttrs
      .slice(9)
      .filter((a) => {
        const id = String(a.attributeId);
        return isLinkedRequired(id) || hintIds.has(id);
      })
      .filter((a) => !headIds.has(String(a.attributeId)));
    return extraTail.length ? [...head, ...extraTail] : head;
  }, [
    attrsExpanded,
    optionalAttrs,
    linkedAttrRules,
    refChangePreviews.productAttributes,
    refVersionConflicts.productAttributes,
  ]);

  const attrVersionHintById = useMemo(
    () => indexAttributeChangeHints(
      refChangePreviews.productAttributes,
      !!refVersionConflicts.productAttributes,
    ),
    [refChangePreviews.productAttributes, refVersionConflicts.productAttributes],
  );

  if (!officialMode && !sheinShopCtx) {
    return (
      <div className="spo-wizard spo-publish">
        <p className="spo-muted">{t('templateEditor.openShein')}</p>
      </div>
    );
  }

  return (
    <div className={`spo-wizard spo-publish${savingTemplate ? ' is-publishing' : ''}`}>
      {savingTemplate ? (
        <div className="spo-publish-loading" role="status" aria-live="polite" aria-busy="true">
          <div className="spo-publish-loading__card">
            <i className="spo-publish-loading__spinner" aria-hidden />
            <strong>{t('templateEditor.saving')}</strong>
            <span>{t('templateEditor.savingHint')}</span>
          </div>
        </div>
      ) : null}
      <aside className="spo-wizard__steps" aria-label={t('templateEditor.sectionsAria')}>
        <div className="spo-wizard__steps-label">
          {t('productTemplate.filePrefix')}
        </div>
        {SECTIONS.map((item, idx) => (
          <button
            key={item.id}
            type="button"
            className={`spo-wizard__step${activeSection === item.id ? ' spo-wizard__step--active' : ''}`}
            onClick={() => scrollToSection(item.id)}
          >
            <span className="spo-wizard__step-index">{idx + 1}</span>
            <span className="spo-wizard__step-title">{t(SECTION_TITLE_KEYS[item.id], { defaultValue: item.title })}</span>
          </button>
        ))}
        <div className="spo-publish__progress">
          <div className="spo-publish__progress-label">{t('tiktokPublish.progress', { percent: completion })}</div>
          <div className="spo-publish__progress-bar">
            <i style={{ width: `${completion}%` }} />
          </div>
        </div>
      </aside>

      <section className="spo-wizard__panel">
        <div className="spo-wizard__panel-scroll">
          {templateLoading ? <p className="spo-wizard__inline-hint">{t('templateEditor.loading')}</p> : null}
          {fieldErrors.form ? (
            <div className="spo-publish-form-error" role="alert" data-field-error="form">
              {fieldErrors.form}
            </div>
          ) : null}

          {/* —— 基础信息 —— */}
          <div id="spo-pub-basic" className="spo-wizard__block" onFocusCapture={() => setActiveSection('basic')}>
            <h3>{t('publishChrome.sectionBasic')}</h3>

            <div className="spo-wizard__form">
              {officialMode ? (
                <div className="spo-wizard__form spo-wizard__form--3 spo-field--full">
                  <label className="spo-field">
                    <span className="spo-field__label">
                      {t('shop.shopType')} <RequiredMark />
                    </span>
                    <select
                      className="spo-select"
                      aria-label={t('shop.shopType')}
                      value={shopType || ''}
                      disabled={templateLoading || savingTemplate}
                      onChange={(e) => handleOfficialShopTypeChange(e.target.value)}
                    >
                      <option value="" disabled>
                        {t('shop.needShopType')}
                      </option>
                      {SHEIN_SHOP_TYPE_OPTIONS.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className={`spo-field${versionConflict ? ' spo-field--version-conflict' : ''}`}>
                    <span className="spo-field__label">
                      {t('productTemplate.colName')} <RequiredMark />
                      <TemplateVersionConflictMark
                        active={versionConflict}
                        label={t('photoLib.updatedMark')}
                        reloading={reloadingVersion}
                        onReload={reloadTemplateFromServer}
                      />
                    </span>
                    <input
                      className="spo-input"
                      value={templateMeta.name}
                      disabled={templateLoading || savingTemplate}
                      maxLength={128}
                      placeholder={t('templateEditor.namePh')}
                      onChange={(e) => setTemplateMeta((prev) => ({ ...prev, name: e.target.value }))}
                    />
                  </label>
                  <label className="spo-field">
                    <span className="spo-field__label">
                      {t('productTemplate.colStyleNo')} <RequiredMark />
                    </span>
                    <input
                      className="spo-input"
                      value={templateMeta.styleNo}
                      disabled={templateLoading || savingTemplate}
                      maxLength={128}
                      placeholder={t('templateEditor.stylePhShein')}
                      onChange={(e) => setTemplateMeta((prev) => ({ ...prev, styleNo: e.target.value }))}
                    />
                  </label>
                </div>
              ) : (
                <>
                  <label className={`spo-field spo-field--full${versionConflict ? ' spo-field--version-conflict' : ''}`}>
                    <span className="spo-field__label">
                      {t('productTemplate.colName')} <RequiredMark />
                      <TemplateVersionConflictMark
                        active={versionConflict}
                        label={t('photoLib.updatedMark')}
                        reloading={reloadingVersion}
                        onReload={reloadTemplateFromServer}
                      />
                    </span>
                    <input
                      className="spo-input"
                      value={templateMeta.name}
                      disabled={templateLoading || savingTemplate}
                      maxLength={128}
                      placeholder={t('templateEditor.namePh')}
                      onChange={(e) => setTemplateMeta((prev) => ({ ...prev, name: e.target.value }))}
                    />
                  </label>
                  <label className="spo-field spo-field--full">
                    <span className="spo-field__label">
                      {t('productTemplate.colStyleNo')} <RequiredMark />
                    </span>
                    <input
                      className="spo-input"
                      value={templateMeta.styleNo}
                      disabled={templateLoading || savingTemplate}
                      maxLength={128}
                      placeholder={t('templateEditor.stylePhShein')}
                      onChange={(e) => setTemplateMeta((prev) => ({ ...prev, styleNo: e.target.value }))}
                    />
                  </label>
                </>
              )}
              <label className="spo-field spo-field--full">
                <span className="spo-field__label">{t('productTemplate.colRemark')}</span>
                <input
                  className="spo-input"
                  value={templateMeta.remark}
                  disabled={templateLoading || savingTemplate}
                  maxLength={200}
                  placeholder={t('templateEditor.remarkPh')}
                  onChange={(e) => setTemplateMeta((prev) => ({ ...prev, remark: e.target.value }))}
                />
              </label>
              <div className="spo-field spo-field--full spo-template-auto-rule">
                <div className="spo-template-auto-rule__switch">
                  <SheinSwitch
                    checked={!!templateMeta.autoRuleEnabled}
                    disabled={templateLoading || savingTemplate}
                    ariaLabel={t('productTemplate.colAutoRule')}
                    onChange={(next) => setTemplateMeta((prev) => ({ ...prev, autoRuleEnabled: next }))}
                  />
                  <span>{t('productTemplate.colAutoRule')}</span>
                </div>
                <p className="spo-wizard__inline-hint">
                  {t('templateEditor.autoHint')}
                </p>
                <label className="spo-field">
                  <span className="spo-field__label">{t('productTemplate.colPriority')}</span>
                  <input
                    className="spo-input"
                    type="number"
                    step={1}
                    value={templateMeta.sortOrder}
                    disabled={templateLoading || savingTemplate}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      setTemplateMeta((prev) => ({
                        ...prev,
                        sortOrder: Number.isFinite(next) ? next : 0,
                      }));
                    }}
                  />
                </label>
              </div>

              <label className={`spo-field spo-field--full${fieldErrors.categoryId ? ' spo-field--invalid' : ''}`} data-field-error="categoryId">
                <span className="spo-field__label">
                  {t('publishBasic.category')}
                  {!officialMode ? <RequiredMark /> : null}
                </span>
                <CategoryCascaderSelect
                  categories={meta.categories}
                  value={form.categoryId}
                  label={form.categoryPathLabel}
                  disabled={(!officialMode && !shopId) || metaLoading || templateLoading || savingTemplate}
                  placeholder={
                    officialMode
                      ? (metaLoading ? t('templateEditor.loadingCats') : t('publishBasic.pickCategory'))
                      : (!shopId ? t('returnList.pickShop') : t('publishBasic.pickCategory'))
                  }
                  onChange={onCategoryChange}
                />

                <FieldErrorText message={fieldErrors.categoryId} />
              </label>

              <label className={`spo-field spo-field--full${fieldErrors.title ? ' spo-field--invalid' : ''}`} data-field-error="title">
                <span className="spo-field__label">
                  {t('publishBasic.productTitle')}
                  {!officialMode ? <RequiredMark /> : null}
                </span>
                <textarea
                  className="spo-textarea spo-textarea--title"
                  value={form.title}
                  maxLength={150}
                  rows={2}
                  onChange={(e) => update('title', e.target.value)}
                  placeholder={t('publishBasic.titlePhShein')}
                />
                <span className="spo-publish__counter">{String(form.title || '').length}/150</span>
                <FieldErrorText message={fieldErrors.title} />
              </label>

              <label className="spo-field">
                <span className="spo-field__label">{t('publishBasic.titleLang')}</span>
                <SearchableSelect
                  value={form.titleLanguage}
                  allowClear={false}
                  onChange={(v) => update('titleLanguage', v)}
                  placeholder={t('publishBasic.pickLang')}
                  options={localizeOptions(TITLE_LANGUAGE_OPTIONS, TITLE_LANG_KEYS)}
                />
                <FieldErrorText message={fieldErrors.titleLanguage} />
              </label>
              <label className={`spo-field${fieldErrors.supplierCode ? ' spo-field--invalid' : ''}`} data-field-error="supplierCode">
                <span className="spo-field__label">
                  {t('publishBasic.merchantSku')}
                  <RequiredMark />
                </span>
                <input
                  className="spo-input"
                  value={form.supplierCode}
                  onChange={(e) => onSupplierCodeChange(e.target.value)}
                  placeholder={t('publishBasic.merchantSkuPh')}
                />

                <FieldErrorText message={fieldErrors.supplierCode} />
              </label>
              <label className={`spo-field${fieldErrors.brandCode ? ' spo-field--invalid' : ''}`} data-field-error="brandCode">
                <span className="spo-field__label">{t('publishBasic.brand')}</span>
                <SearchableSelect
                  value={form.brandCode}
                  disabled={!shopId || metaLoading}
                  placeholder={t('publishBasic.brandPhShein')}
                  onChange={(v) => update('brandCode', v)}
                  options={brandOptions
                    .filter((b) => b.brandCode && b.brandCode !== '—')
                    .map((opt) => ({
                      value: opt.brandCode,
                      label: opt.brandName !== '—' ? opt.brandName : opt.brandCode,
                    }))}
                />
                <FieldErrorText message={fieldErrors.brandCode} />
              </label>
              <label className="spo-field">
                <span className="spo-field__label">{t('publishBasic.origin')}</span>
                <SearchableSelect
                  value={form.originCountry}
                  placeholder={t('publishBasic.originPhShein')}
                  onChange={(v) => update('originCountry', v)}
                  options={ORIGIN_OPTIONS.map((c) => ({ value: c, label: c }))}
                />
              </label>
              <label className="spo-field">
                <span className="spo-field__label">{t('publishBasic.productIp')}</span>
                <SearchableSelect
                  value={form.ipId}
                  disabled={!shopId || metaLoading}
                  placeholder={t('publishBasic.pickIp')}
                  onChange={onIpChange}
                  options={ipOptions
                    .filter((i) => i.ipId && i.ipId !== '—')
                    .map((opt) => ({
                      value: opt.ipId,
                      label: opt.ipNameCn !== '—' ? opt.ipNameCn : opt.ipName,
                    }))}
                />
              </label>
              <label className={`spo-field spo-field--full${fieldErrors.description ? ' spo-field--invalid' : ''}`} data-field-error="description">
                <span className="spo-field__label">{t('publishBasic.desc')}</span>
                <textarea className="spo-textarea" value={form.description} onChange={(e) => update('description', e.target.value)} placeholder={t('publishBasic.descPhShein')} />
                <FieldErrorText message={fieldErrors.description} />
              </label>
            </div>
          </div>

          {/* —— 类目属性 —— */}
          <div
            id="spo-pub-attrs"
            className="spo-wizard__block"
            onFocusCapture={() => setActiveSection('attrs')}
            data-field-error="productAttributes"
          >
            <div className="spo-publish__block-head">
              <h3>{t('publishChrome.sectionAttrs')}</h3>
            </div>
            <FieldErrorText message={fieldErrors.productAttributes} />
            {!form.categoryId ? (
              <p className="spo-wizard__inline-hint">{t('publishAttr.pickCategory')}</p>
            ) : attrLoading ? (
              <div className="spo-attr-loading" role="status" aria-live="polite" aria-busy="true">
                <div className="spo-attr-loading__banner">
                  <span className="spo-attr-loading__spinner" aria-hidden />
                  <div className="spo-attr-loading__copy">
                    <strong>{t('publishAttr.loadingTitle')}</strong>
                    <span>{t('publishAttr.loadingHint')}</span>
                  </div>
                </div>
                <div className="spo-wizard__form spo-wizard__form--attrs spo-attr-loading__skeleton">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="spo-attr-loading__card">
                      <span className="spo-attr-loading__line spo-attr-loading__line--label" />
                      <span className="spo-attr-loading__line spo-attr-loading__line--field" />
                    </div>
                  ))}
                </div>
              </div>
            ) : attrError && !(requiredAttrs.length || optionalAttrs.length) ? (
              <p className="spo-wizard__inline-error" role="alert">{attrError}</p>
            ) : (
              <>
                {attrError ? <p className="spo-wizard__inline-hint">{attrError}</p> : null}
                {allProductAttrs.length > 0 ? (
                  <div className={`spo-size-chart-template-bar spo-attr-template-bar${refVersionConflicts.productAttributes ? ' spo-attr-template-bar--version-conflict' : ''}`}>
                    <TemplateVersionConflictMark
                      active={!!refVersionConflicts.productAttributes}
                      label={
                        refChangePreviews.productAttributes?.count != null
                          ? t('publishAttr.updatedCount', { count: refChangePreviews.productAttributes.count })
                          : t('photoLib.updatedMark')
                      }
                      reloading={reloadingRefType === TEMPLATE_VERSION_TYPES.attribute}
                      resolveChangePreview={async () => (
                        refChangePreviews.productAttributes
                        || buildAttrChangePreview()
                      )}
                      onReload={() => reloadReferencedTemplateRegion(TEMPLATE_VERSION_TYPES.attribute)}
                    />
                    <select
                      className="spo-select"
                      value={selectedAttrTemplateId}
                      disabled={attrTemplateBusy || (!officialMode && !shopId)}
                      onChange={(e) => onAttrTemplateSelect(e.target.value)}
                    >
                      <option value="">{t('publishAttr.pickLib')}</option>
                      {attrTemplates.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                          {item.filledAttrCount != null ? t('publishAttr.filledCount', { count: item.filledAttrCount }) : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="spo-btn spo-btn--primary spo-btn--sm"
                      disabled={attrTemplateBusy || (!officialMode && !shopId) || !form.categoryId}
                      onClick={openAttrSaveModal}
                    >
                      {t('shop.save')}
                    </button>
                  </div>
                ) : null}
                {requiredAttrs.length > 0 ? (
                  <>
                    <h4 className="spo-publish__subhead">
                      {t('publishAttr.requiredAttrs')}
                      {linkedAttrLoading ? <span className="spo-wizard__inline-hint">{t('publishAttr.linkSync')}</span> : null}
                    </h4>
                    <div className="spo-wizard__form spo-wizard__form--attrs">
                      {layoutAttributeFields(requiredAttrs).map(({ attr, fullWidth }) => (
                        <AttributeField
                          key={attr.attributeId}
                          attr={attr}
                          required={isSheinAttrRequiredMark(attr, requiredAttrs, form.productAttributes)}
                          fullWidth={fullWidth}
                          shopId={shopId}
                          categoryId={form.categoryId}
                          allowCustomValue={!!customAttrPerm[String(attr.attributeId)]}
                          allowedValueIds={linkedAllowedIds(attr.attributeId)}
                          value={form.productAttributes[attr.attributeId]}
                          onChange={(v) => setAttrValue(attr.attributeId, v)}
                          onCustomValueAdded={appendCustomAttrValue}
                          versionUpdateHint={attrVersionHintById[String(attr.attributeId)] || null}
                        />
                      ))}
                    </div>
                  </>
                ) : null}

                {optionalAttrs.length > 0 ? (
                  <>
                    <h4 className="spo-publish__subhead">
                      {t('publishAttr.optionalAttrs')}
                      {!requiredAttrs.length && linkedAttrLoading
                        ? <span className="spo-wizard__inline-hint">{t('publishAttr.linkSync')}</span>
                        : null}
                    </h4>
                    <div className="spo-wizard__form spo-wizard__form--attrs">
                      {layoutAttributeFields(visibleOptional).map(({ attr, fullWidth }) => (
                        <AttributeField
                          key={attr.attributeId}
                          attr={attr}
                          required={isLinkedRequired(attr.attributeId)}
                          fullWidth={fullWidth}
                          shopId={shopId}
                          categoryId={form.categoryId}
                          allowCustomValue={!!customAttrPerm[String(attr.attributeId)]}
                          allowedValueIds={linkedAllowedIds(attr.attributeId)}
                          value={form.productAttributes[attr.attributeId]}
                          onChange={(v) => setAttrValue(attr.attributeId, v)}
                          onCustomValueAdded={appendCustomAttrValue}
                          versionUpdateHint={attrVersionHintById[String(attr.attributeId)] || null}
                        />
                      ))}
                    </div>
                    {optionalAttrs.length > 9 ? (
                      <div className="spo-publish__expand">
                        <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" onClick={() => setAttrsExpanded((v) => !v)}>
                          {attrsExpanded ? t('returnDetail.collapse') : t('publishAttr.expand', { count: optionalAttrs.length - 9 })}
                        </button>
                      </div>
                    ) : null}
                  </>
                ) : null}

                {!requiredAttrs.length && !optionalAttrs.length ? (
                  <p className="spo-wizard__inline-hint">{t('publishAttr.none')}</p>
                ) : null}
              </>
            )}
          </div>

          {/* —— 规格供应 —— */}
          <div id="spo-pub-spec" className="spo-wizard__block" onFocusCapture={() => setActiveSection('spec')} data-field-error="skcList">
            <h3>{t('publishChrome.sectionSpecTitle')}</h3>
            <FieldErrorText message={fieldErrors.skcList || fieldErrors.supplierSku || fieldErrors.supply} />
            <SpecSupplySection
              colorAttr={colorAttr}
              sizeAttr={sizeAttr}
              skcList={form.skcList}
              selectedSizeIds={form.selectedSizeIds || []}
              shopId={shopId}
              skipSheinConvert={officialMode}
              categoryId={form.categoryId}
              customAttrPerm={customAttrPerm}
              onCustomValueAdded={appendCustomAttrValue}
              currency={platformCurrency}
              currencyPending={publishCurrencyLoading || !platformCurrency}
              priceMode={priceMode}
              attrLoading={attrLoading}
              defaultSupplierSkuSpu={form.supplierCode || ''}
              printImages={form.printImages || []}
              onPrintImagesChange={updatePrintImages}
              onColorsChange={onColorsChange}
              onSizesChange={onSizesChange}
              onUpdateSkc={updateSkc}
              onUpdateSku={updateSku}
              onRemoveSku={removeSku}
              onBatchUpdateSku={batchUpdateSku}
            />
          </div>

          {/* —— 尺码包装 —— */}
          <div
            id="spo-pub-size"
            className="spo-wizard__block"
            onFocusCapture={() => setActiveSection('size')}
            data-field-error="sizeChart"
          >
            <SizeChartPanel
              shopId={shopId}
              officialMode={officialMode}
              shopType={shopType}
              productTypeId={form.productTypeId}
              categoryId={form.categoryId}
              categoryPathLabel={form.categoryPathLabel}
              supplierCode={form.supplierCode}
              sizeSaleAttributeId={form.sizeSaleAttributeId || sizeAttr?.attributeId || ''}
              sizeMeasureAttrs={sizeMeasureAttrs}
              sizeAttr={sizeAttr}
              selectedSizeIds={form.selectedSizeIds || []}
              skcList={form.skcList}
              sizeChart={form.sizeChart || {}}
              errorMessage={fieldErrors.sizeChart}
              onChangeCell={updateSizeCell}
              onApplyChart={applySizeChart}
              selectedTemplateId={selectedSizeChartTemplateId}
              onSelectedTemplateIdChange={setSelectedSizeChartTemplateId}
              onSelectedTemplateVersionChange={setSelectedSizeChartTemplateVersion}
              versionConflict={!!refVersionConflicts.sizeChart}
              conflictLabel={
                refChangePreviews.sizeChart?.count != null
                  ? t('publishAttr.updatedCount', { count: refChangePreviews.sizeChart.count })
                  : t('photoLib.updatedMark')
              }
              resolveChangePreview={async () => (
                refChangePreviews.sizeChart || buildSizeChartChangePreview()
              )}
              reloadingVersion={reloadingRefType === TEMPLATE_VERSION_TYPES.sizeChart}
              onReloadVersionConflict={() => reloadReferencedTemplateRegion(TEMPLATE_VERSION_TYPES.sizeChart)}
              allowSaveToLibrary
            />
            {refVersionConflicts.sizeChart && Array.isArray(refChangePreviews.sizeChart?.changes)
              && refChangePreviews.sizeChart.changes.length > 0 ? (
              <div className="spo-version-conflict-summary">
                <TemplateChangeDiffList
                  intro={t('templateEditor.sizeDiffReload', { count: refChangePreviews.sizeChart.count })}
                  changes={refChangePreviews.sizeChart.changes.slice(0, 8)}
                />
              </div>
            ) : null}

            <h4 className="spo-publish__subhead">{t('publishChrome.sectionSize')}</h4>
            <div className="spo-publish__table-wrap spo-publish__table-wrap--scroll">
              <table className="spo-publish__table spo-pkg-table">
                <colgroup>
                  <col className="spo-pkg-col spo-pkg-col--color" />
                  <col className="spo-pkg-col spo-pkg-col--size" />
                  <col className="spo-pkg-col spo-pkg-col--weight" />
                  <col className="spo-pkg-col spo-pkg-col--dims" />
                  {showSkuQuantity ? <col className="spo-pkg-col spo-pkg-col--qty" /> : null}
                  {showSkuPackageType ? <col className="spo-pkg-col spo-pkg-col--pkg-type" /> : null}
                </colgroup>
                <thead>
                  <tr>
                    <th>{colorLabel}</th>
                    <th>{sizeLabel}</th>
                    <th className="spo-pkg-table__weight">
                      <span className="spo-supply-th">
                        <span>{t('publishPkg.weight')}</span>
                        <button
                          type="button"
                          className="spo-supply-th__batch"
                          onClick={() => setPkgBatchMode('weight')}
                        >
                          {t('publishSpec.batch')}
                        </button>
                      </span>
                    </th>
                    <th className="spo-pkg-table__dims">
                      <span className="spo-supply-th">
                        <span>{t('publishPkg.pkgSize')}</span>
                        <button
                          type="button"
                          className="spo-supply-th__batch"
                          onClick={() => setPkgBatchMode('dims')}
                        >
                          {t('publishSpec.batch')}
                        </button>
                      </span>
                    </th>
                    {showSkuQuantity ? (
                      <th className="spo-pkg-table__qty">
                        <span className="spo-supply-th">
                          <span>{requireSkuQuantity ? <>{t('publishPkg.pkgQty')} <RequiredMark /></> : t('publishPkg.pkgQty')}</span>
                          <button
                            type="button"
                            className="spo-supply-th__batch"
                            onClick={() => setPkgBatchMode('quantity')}
                          >
                            {t('publishSpec.batch')}
                          </button>
                        </span>
                      </th>
                    ) : null}
                    {showSkuPackageType ? (
                      <th className="spo-pkg-table__pkg-type">
                        <span className="spo-supply-th">
                          <span>{requireSkuPackageType ? <>{t('publishPkg.pkgType')} <RequiredMark /></> : t('publishPkg.pkgType')}</span>
                          <button
                            type="button"
                            className="spo-supply-th__batch"
                            onClick={() => setPkgBatchMode('packageType')}
                          >
                            {t('publishSpec.batch')}
                          </button>
                        </span>
                      </th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {form.skcList.flatMap((skc) => skc.skuRows.map((sku) => (
                    <tr key={`${skc.id}-${sku.id}`}>
                      <td>{skc.colorLabel || '—'}</td>
                      <td>
                        {sku.sizeIsCustom || sku.sizeExtraValue
                          ? (attributeValueDisplayLabel(sku.sizeLabel || sku.sizeExtraValue) || '—')
                          : (
                            attributeValueDisplayLabel(
                              sizeAttr?.values?.find((v) => String(v.id) === String(sku.sizeValueId))
                              || sku.sizeLabel,
                            ) || '—'
                          )}
                      </td>
                      <td className="spo-pkg-table__weight">
                        <div className="spo-pkg-inline">
                          <input
                            className="spo-input spo-pkg-inline__input"
                            value={sku.weight ?? ''}
                            onChange={(e) => updateSku(skc.id, sku.id, { weight: e.target.value })}
                            placeholder={t('publishPkg.phWeight')}
                          />
                          <SearchableSelect
                            className="spo-select--unit"
                            value={form.weightUnit || 'g'}
                            allowClear={false}
                            onChange={(v) => update('weightUnit', v)}
                            options={WEIGHT_UNIT_OPTIONS}
                          />
                        </div>
                      </td>
                      <td className="spo-pkg-table__dims">
                        <div className="spo-pkg-inline spo-pkg-inline--dims">
                          <input
                            className="spo-input spo-pkg-inline__input"
                            value={sku.length ?? ''}
                            onChange={(e) => updateSku(skc.id, sku.id, { length: e.target.value })}
                            placeholder={t('publishPkg.len')}
                          />
                          <span className="spo-pkg-inline__x">×</span>
                          <input
                            className="spo-input spo-pkg-inline__input"
                            value={sku.width ?? ''}
                            onChange={(e) => updateSku(skc.id, sku.id, { width: e.target.value })}
                            placeholder={t('publishPkg.wid')}
                          />
                          <span className="spo-pkg-inline__x">×</span>
                          <input
                            className="spo-input spo-pkg-inline__input"
                            value={sku.height ?? ''}
                            onChange={(e) => updateSku(skc.id, sku.id, { height: e.target.value })}
                            placeholder={t('publishPkg.hei')}
                          />
                          <SearchableSelect
                            className="spo-select--unit"
                            value={form.dimUnit || 'cm'}
                            allowClear={false}
                            onChange={(v) => update('dimUnit', v)}
                            options={DIM_UNIT_OPTIONS}
                          />
                        </div>
                      </td>
                      {showSkuQuantity ? (
                        <td className="spo-pkg-table__qty">
                          <div className="spo-pkg-inline">
                            <input
                              className="spo-input spo-pkg-inline__input spo-pkg-inline__input--qty"
                              value={sku.quantity ?? ''}
                              inputMode="numeric"
                              onChange={(e) => updateSku(skc.id, sku.id, { quantity: e.target.value })}
                              placeholder={t('publishPkg.phQty')}
                            />
                            <SearchableSelect
                              className="spo-select--unit"
                              value={sku.quantityUnit || '1'}
                              allowClear={false}
                              onChange={(v) => updateSku(skc.id, sku.id, { quantityUnit: v })}
                              options={localizeOptions(QUANTITY_UNIT_OPTIONS, SHEIN_QTY_KEYS)}
                            />
                          </div>
                        </td>
                      ) : null}
                      {showSkuPackageType ? (
                        <td className="spo-pkg-table__pkg-type">
                          <SearchableSelect
                            value={sku.packageType === 0 || sku.packageType === '0' ? '' : (sku.packageType ?? '')}
                            allowClear
                            placeholder={t('shop.pleaseSelect')}
                            onChange={(v) => updateSku(skc.id, sku.id, {
                              packageType: v == null || v === '' ? '' : String(v),
                            })}
                            options={localizeOptions(PACKAGE_TYPE_OPTIONS, SKU_PKG_TYPE_KEYS)}
                          />
                        </td>
                      ) : null}
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>
            {pkgBatchMode ? (
              <PackageBatchModal
                mode={pkgBatchMode}
                skcList={form.skcList}
                sizeAttr={sizeAttr}
                weightUnit={form.weightUnit || 'g'}
                dimUnit={form.dimUnit || 'cm'}
                onClose={() => setPkgBatchMode(null)}
                onConfirm={(result) => {
                  if (!result?.applyAll && !result?.bySize) {
                    setPkgBatchMode(null);
                    return;
                  }
                  setForm((prev) => {
                    const next = {
                      ...prev,
                      skcList: result.applyAll
                        ? applyPackageBatchToAllSkus(prev.skcList, result.patch)
                        : applyPackageBatchBySize(prev.skcList, result.bySize),
                    };
                    if (pkgBatchMode === 'weight' && result.weightUnit) {
                      next.weightUnit = String(result.weightUnit);
                    }
                    if (pkgBatchMode === 'dims' && result.dimUnit) {
                      next.dimUnit = String(result.dimUnit);
                    }
                    return next;
                  });
                  showToast(
                    result.applyAll ? t('publishPkg.appliedAll') : t('publishPkg.appliedBySize'),
                    'success',
                  );
                  setPkgBatchMode(null);
                }}
              />
            ) : null}
          </div>

          {/* —— 销售发货 —— */}
          <div id="spo-pub-sales" className="spo-wizard__block" onFocusCapture={() => setActiveSection('sales')}>
            <h3>{t('publishChrome.sectionSalesInfo')}</h3>
            <div className={`spo-field spo-field--full${fieldErrors.selectedSites ? ' spo-field--invalid' : ''}`} data-field-error="selectedSites">
              <span className="spo-field__label">
                {t('tiktokPublish.listingSites')}
                {!officialMode ? <RequiredMark /> : null}
              </span>
              <SearchableMultiSelect
                value={form.selectedSites || []}
                onChange={onSitesChange}
                placeholder={metaLoading ? t('tiktokPublish.loading') : t('publishSales.pickSitesMulti')}
                disabled={metaLoading}
                emptyText={metaLoading
                  ? t('tiktokPublish.loading')
                  : (siteOptions.length === 0
                    ? (officialMode ? t('templateEditor.sitesSyncAny') : t('tiktokPublish.noSitesYet'))
                    : t('tiktokPublish.noSitesYet'))}
                options={siteOptions.map((site) => ({
                  value: site.siteAbbr,
                  // 不展示站点列表币种，避免与供货价规范币种混淆
                  label: site.siteName || site.siteAbbr,
                }))}
              />
              <FieldErrorText message={fieldErrors.selectedSites} />
            </div>

            {/* 币种不单独展示：半托管/全托管由发布规范写入，自运营由站点列表写入 form.currency */}

            <h4 className="spo-publish__subhead">{t('tiktokPublish.shelfWay')}</h4>
            <div className="spo-publish__table-wrap">
              <table className="spo-publish__table spo-shelf-table">
                <colgroup>
                  <col className="spo-shelf-col spo-shelf-col--color" />
                  <col className="spo-shelf-col spo-shelf-col--way" />
                  <col className="spo-shelf-col spo-shelf-col--time" />
                </colgroup>
                <thead>
                  <tr>
                    <th>{colorLabel}</th>
                    <th>{t('tiktokPublish.shelfWay')}</th>
                    <th>{t('publishSales.hopeAt')}</th>
                  </tr>
                </thead>
                <tbody>
                  {form.skcList.map((skc) => (
                    <tr key={skc.id}>
                      <td>{skc.colorLabel || t('publishSales.unselected', { label: colorLabel })}</td>
                      <td>
                        <SearchableSelect
                          value={form.shelfWay}
                          allowClear={false}
                          onChange={(v) => update('shelfWay', v)}
                          options={[
                            { value: '1', label: t('publishSales.shelfNow') },
                            { value: '2', label: t('publishSales.shelfTimed') },
                          ]}
                        />
                      </td>
                      <td>
                        {form.shelfWay === '2' ? (
                          <SheinDateTimePicker
                            value={form.hopeOnSaleDate}
                            onChange={(v) => update('hopeOnSaleDate', v)}
                            placeholder={t('publishSales.pickHope')}
                          />
                        ) : (
                          <span className="spo-shelf-table__empty">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h4 className="spo-publish__subhead">{t('publishSales.stockWarehouse')}</h4>
            <div className="spo-wizard__form">
              <div className="spo-field spo-field--full" data-field-error="warehouseId">
                <span className="spo-field__label">
                  {t('publishSales.stockWarehouse')}{multiWarehouse ? <RequiredMark /> : null}
                </span>
                {metaLoading ? (
                  <p className="spo-wizard__inline-hint">{t('tiktokPublish.loadingWarehouses')}</p>
                ) : officialMode || multiWarehouse || warehouseOptions.length === 0 ? (
                  <>
                    <SearchableSelect
                      value={form.warehouseId}
                      onChange={onWarehouseChange}
                      allowClear={officialMode}
                      placeholder={warehouseOptions.length === 0
                        ? (officialMode ? t('templateEditor.noWhSync') : t('tiktokPublish.noWarehouseOption'))
                        : t('publishSales.pickStockWh')}
                      emptyText={officialMode
                        ? t('templateEditor.noWhSync')
                        : t('tiktokPublish.noWarehouseOption')}
                      options={warehouseOptions}
                      disabled={warehouseOptions.length === 0 && !officialMode}
                    />
                    {warehouseOptions.length === 0 ? (
                      <p className="spo-wizard__inline-hint">
                        {officialMode
                          ? t('templateEditor.officialWhCache')
                          : t('templateEditor.noWhData')}
                      </p>
                    ) : multiWarehouse || officialMode ? (
                      <p className="spo-wizard__inline-hint">
                        {officialMode
                          ? t('templateEditor.officialWhMatch')
                          : t('publishSales.multiWhHint')}
                      </p>
                    ) : null}
                  </>
                ) : (
                  <p className="spo-wizard__inline-hint">
                    {t('publishSales.defaultWh', { name: warehouseOptions[0]?.label || form.warehouseId })}
                  </p>
                )}
                <FieldErrorText message={fieldErrors.warehouseId} />
              </div>
            </div>
          </div>
        </div>

        <div className="spo-wizard__footer">
          <button
            type="button"
            className="spo-btn spo-btn--ghost"
            onClick={() => navigate(
              officialMode && afterSaveNavigate
                ? afterSaveNavigate
                : buildSheinPath('templates/product'),
            )}
            disabled={savingTemplate || templateLoading}
          >
            {t('templateEditor.backList')}
          </button>
          <div className="spo-wizard__footer-right">
            {!officialMode ? (
              <button
                type="button"
                className="spo-btn spo-btn--secondary"
                onClick={onPublishFromTemplate}
                disabled={savingTemplate || templateLoading || !templateHydrated || !shopId}
              >
                {t('productTemplate.publish')}
              </button>
            ) : null}
            <button
              type="button"
              className="spo-btn spo-btn--primary"
              onClick={onSaveProductTemplate}
              disabled={savingTemplate || templateLoading || !templateHydrated || (!officialMode && !shopId)}
            >
              {savingTemplate ? t('tiktokPublish.saving') : t('shop.save')}
            </button>
          </div>
        </div>
        <PublishScrollJumpFab prefix="spo" />
      </section>

      <SaveAttributeTemplateMetaModal
        open={attrSaveModalOpen}
        shopId={shopId}
        officialMode={officialMode}
        shopType={shopType}
        categoryId={form.categoryId}
        productTypeId={form.productTypeId}
        categoryPathLabel={form.categoryPathLabel}
        productAttributes={form.productAttributes}
        attrDefs={allProductAttrs}
        onClose={() => setAttrSaveModalOpen(false)}
        onSaved={refreshAttrTemplates}
      />
    </div>
  );
}
